import axios, { AxiosError, AxiosHeaders } from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import type { AthleteProfileOut } from "./apiValidation";
import { getConversationId } from "./utils";
import { getBaseURL } from "./api-base-url";
import type { ClimateExpectation, CompletedActivity, PlannedWorkout } from "@/types";
import type { WeeklySummaryCard } from "@/types/calendar";
import { calendarApi, activitiesApi, authApi, settingsApi } from "./api/typedClient";
import { log } from './logger';
export { getBaseURL };

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: unknown;
  /** Request URL that failed (for debugging 4xx/5xx). */
  requestUrl?: string;
}

// Confirmation types for PROPOSAL_ONLY responses
export interface ProposalOnlyResponse {
  status: "PROPOSAL_ONLY";
  action: "create" | "modify" | "delete";
  resource_type: "session" | "week" | "season";
  summary: string;
  diff?: {
    before?: unknown;
    after?: unknown;
    changes?: Array<{
      field: string;
      before: unknown;
      after: unknown;
    }>;
  };
  proposal_id?: string;
  message?: string;
}

export interface WriteResponse<T = unknown> {
  status: "ok" | "PROPOSAL_ONLY";
  data?: T;
  proposal?: ProposalOnlyResponse;
}

// Calendar types
export interface WorkoutStep {
  order: number;
  name: string;
  duration_min: number | null;
  distance_km: number | null;
  intensity: string | null;
  notes: string | null;
}

export interface CoachVerdict {
  type: "ok" | "modify" | "rest" | "caution";
  reason: string;
  confidence?: number;
  generated_at: string;
}

export interface LLMFeedback {
  text: string;
  tone: "neutral" | "encouraging" | "corrective";
  generated_at: string; // ISO 8601 timestamp
}

export interface ExecutionStateInfo {
  state: "unexecuted" | "executed_as_planned" | "executed_unplanned" | "missed";
  reason?: string | null;
  deltas?: Record<string, number | null> | null;
  resolved_at?: string | null;
  llm_feedback?: LLMFeedback | null;
}

export interface CalendarSession {
  id: string;
  date: string;
  time: string | null;
  type: string;
  title: string;
  duration_minutes: number | null;
  distance_km: number | null;
  intensity: string | null;
  status: "planned" | "completed" | "skipped" | "deleted" | "missed";
  notes: string | null;
  execution_notes?: string | null;
  // PHASE F3: Calendar API should return workout_id
  workout_id?: string | null;
  // Pairing: session is paired if this is non-null
  completed_activity_id?: string | null;
  // Completion tracking fields (robust to inconsistent data)
  completed?: boolean;
  completed_at?: string | null;
  // CRITICAL: Only planned_sessions.id may be mutated. Calendar sessions, workouts, and activities are READ-ONLY views.
  planned_session_id?: string | null;
  // LLM-generated execution content
  instructions?: string[];
  steps?: WorkoutStep[];
  coach_insight?: string | null;
  must_dos?: string[];
  // Phase 5A: Coach verdict (read-only, advisory)
  coach_verdict?: CoachVerdict | null;
  // PHASE 2.2: Execution state derived centrally
  execution_state?: ExecutionStateInfo | null;
  // Closed-loop workout verdict (from workout_verdicts table)
  verdict?: string | null;
  verdict_confidence?: number | null;
}

export interface TodayResponse {
  date: string;
  sessions: CalendarSession[];
}

export interface WeekResponse {
  week_start: string;
  week_end: string;
  sessions: CalendarSession[];
}

export interface SeasonResponse {
  season_start: string;
  season_end: string;
  sessions: CalendarSession[];
  total_sessions: number;
  completed_sessions: number;
  planned_sessions: number;
}

// Activity streams types
export interface StreamsData {
  time: number[]; // Time in seconds from start
  route_points: number[][]; // GPS coordinates [[lat, lng], ...]
  elevation: number[]; // Elevation in meters
  pace: (number | null)[]; // Pace in min/km (null when stopped)
  heartrate?: number[]; // Heart rate in bpm (if available)
  distance: number[]; // Cumulative distance in meters
  power?: number[]; // Power in watts (if available)
  cadence?: number[]; // Cadence in rpm (if available)
  data_points: number; // Total number of data points
}

export type ActivityStreamsResponse = StreamsData;

// Platform detection for auth strategy
import { isNative } from './platform';

// Axios instance configured for CORS
// - Web: withCredentials: true enables sending cookies/credentials with cross-origin requests
// - Mobile: Uses Bearer tokens in Authorization header (no cookies)
// - Backend CORS is configured to allow requests from https://athletespace.ai and other origins
export const api = axios.create({
  baseURL: getBaseURL(),
  // Only use credentials for web (cookies). Mobile uses Bearer tokens.
  withCredentials: !isNative(), // Disable for mobile, enable for web
  timeout: 60000, // 60 seconds - increased for large activity queries
});

/**
 * Checks if an error is a CORS error
 */
const isCorsError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as { code?: string; message?: string; response?: { status?: number } };
  
  // CORS errors typically show up as:
  // - ERR_NETWORK code
  // - Status 0 or 500 with CORS message
  // - Message containing "CORS" or "Cross-Origin"
  if (err.code === 'ERR_NETWORK') {
    return true;
  }
  
  if (err.message && (
    err.message.includes('CORS') ||
    err.message.includes('Cross-Origin') ||
    err.message.includes('Access-Control-Allow-Origin')
  )) {
    return true;
  }
  
  // Status 500 with ERR_NETWORK is often a CORS issue
  if (err.response?.status === 500 && err.code === 'ERR_NETWORK') {
    return true;
  }
  
  return false;
};

/**
 * Initiates Strava OAuth connection.
 * Fetches OAuth URL from backend and redirects to Strava.
 * 
 * CRITICAL: For native apps, uses Browser.open() to open OAuth in external browser.
 * This prevents WebView reloads.
 */
export const initiateStravaConnect = async (): Promise<void> => {
  log.debug("API", "Initiating Strava connect");
  
  try {
    // Use typed client for contract enforcement
    const response = await authApi.getStravaOAuthUrl();
    const responseData = response.data || response;
    
    const oauthUrl = (responseData as { redirect_url?: string; oauth_url?: string; url?: string }).redirect_url 
      || (responseData as { redirect_url?: string; oauth_url?: string; url?: string }).oauth_url
      || (responseData as { redirect_url?: string; oauth_url?: string; url?: string }).url;
    
    if (!oauthUrl) {
      throw new Error("Backend did not return OAuth URL");
    }

    // Check if running in native app (Capacitor)
    const { isNative } = await import('./platform');
    const isCapacitor = isNative();
    
    // CRITICAL: For native apps, use Browser.open() to open OAuth in external browser
    // This prevents WebView reloads
    if (isCapacitor) {
      const { Browser } = await import('@capacitor/browser');
      log.debug("API", "Opening Strava OAuth in external browser (native)");
      log.debug("API", "Browser.open() URL:", oauthUrl);
      try {
        await Browser.open({ 
          url: oauthUrl,
          presentationStyle: 'popover' // iOS-safe presentation style
        });
        log.debug("API", "Browser.open() completed successfully");
      } catch (error) {
        log.error("API", "Browser.open() failed:", error);
        // Fallback: try window.location as last resort (will cause WebView reload but better than nothing)
        log.warn("API", "Falling back to window.location.href");
        window.location.href = oauthUrl;
      }
    } else {
      // Web: Use standard redirect
      log.debug("API", "Redirecting to Strava OAuth URL (web)");
      window.location.href = oauthUrl;
    }
  } catch (error) {
    log.error("API", "Failed to initiate Strava connect:", error);
    throw error;
  }
};

/**
 * Disconnects Strava integration.
 * Treats as success if:
 * - HTTP 200, OR
 * - Response has connected === false
 * Only throws errors for status >= 500
 */
export const disconnectStrava = async (): Promise<void> => {
  log.debug("API", "Disconnecting Strava");
  try {
    const response = await authApi.disconnectStrava();
    const responseData = response.data || response;
    
    // Check if response indicates disconnection was successful
    const connected = (responseData as { connected?: boolean }).connected;
    
    // Success if HTTP 200 OR connected === false
    if (connected === false) {
      log.debug("API", "Strava disconnected successfully (connected: false)");
      return;
    }
    
    // If we got here, it's HTTP 200 (success)
    log.debug("API", "Strava disconnected successfully (HTTP 200)");
  } catch (error) {
    const apiError = error as { status?: number; message?: string };
    
    // Only throw errors for status >= 500 (server errors)
    // Treat 4xx (client errors) as success - user is already disconnected
    if (apiError.status && apiError.status >= 500) {
      log.error("API", "Failed to disconnect Strava (server error):", error);
      throw error;
    }
    
    // For 4xx errors, check if response indicates already disconnected
    if (apiError.status && apiError.status < 500) {
      // Check if error response has connected === false
      const errorDetails = apiError as { details?: { connected?: boolean } };
      if (errorDetails.details && typeof errorDetails.details === 'object') {
        const connected = (errorDetails.details as { connected?: boolean }).connected;
        if (connected === false) {
          log.debug("API", "Strava already disconnected (connected: false in error response)");
          return;
        }
      }
      
      // For other 4xx errors, still treat as success (user is likely already disconnected)
      log.debug("API", "Strava disconnect treated as success (4xx error, likely already disconnected)");
      return;
    }
    
    // If no status code, it might be a network error - throw it
    log.error("API", "Failed to disconnect Strava (unknown error):", error);
    throw error;
  }
};

/**
 * Initiates Google OAuth connection.
 * Redirects directly to backend OAuth endpoint which handles the OAuth flow.
 * 
 * Note: This function redirects to /auth/google/login?platform=web which
 * returns a 302 redirect to Google's consent screen.
 * 
 * For mobile apps, includes redirect_uri=athletespace://auth/callback to deep link
 * back into the app after OAuth completes.
 * 
 * CRITICAL: For native apps, uses Browser.open() to open OAuth in external browser.
 * This prevents WebView reloads and avoids Google's "disallowed_useragent" error.
 */
export const initiateGoogleConnect = async (): Promise<void> => {
  log.debug("API", "Initiating Google connect");
  
  try {
    // Use the same getBaseURL logic as the main API instance
    const API = getBaseURL();
    
    // Check if running in native app (Capacitor)
    const { isNative } = await import('./platform');
    const isCapacitor = isNative();
    
    // Build OAuth URL with platform and redirect_uri for mobile
    const params = new URLSearchParams();
    params.set('platform', isCapacitor ? 'mobile' : 'web');
    
    // For mobile apps, tell backend to redirect to our custom URL scheme
    if (isCapacitor) {
      params.set('redirect_uri', 'athletespace://auth/callback');
    }
    
    // CRITICAL: Must use backend URL, not frontend domain
    // In production: https://api.athletespace.ai/auth/google/login
    // NOT: /auth/google/login (relative) or https://athletespace.ai/auth/google/login (frontend domain)
    // This endpoint returns a 302 redirect to Google's consent screen
    const url = `${API}/auth/google/login?${params.toString()}`;
    
    log.debug("API", "Google OAuth redirect URL:", url);
    log.debug("API", "API base URL:", API);
    log.debug("API", "Platform:", isCapacitor ? "mobile" : "web");
    
    // Sanity check: In production, URL must point to backend, not frontend
    if (import.meta.env.PROD && typeof window !== 'undefined') {
      const frontendOrigin = window.location.origin;
      if (url.startsWith(frontendOrigin)) {
        const errorMsg = `CRITICAL: Google OAuth URL points to frontend domain (${frontendOrigin}) instead of backend! ` +
                        `This will cause OAuth failures. URL: ${url}`;
        log.error("API", errorMsg);
        throw new Error("Google OAuth URL must point to backend domain, not frontend. Check VITE_API_URL configuration.");
      }
    }
    
    // CRITICAL: For native apps, use Browser.open() to open OAuth in external browser
    // This prevents WebView reloads and avoids Google's "disallowed_useragent" error
    if (isCapacitor) {
      const { Browser } = await import('@capacitor/browser');
      log.debug("API", "Opening Google OAuth in external browser (native)");
      log.debug("API", "Browser.open() URL:", url);
      try {
        await Browser.open({ 
          url,
          presentationStyle: 'popover' // iOS-safe presentation style
        });
        log.debug("API", "Browser.open() completed successfully");
      } catch (error) {
        log.error("API", "Browser.open() failed:", error);
        // Fallback: try window.location as last resort (will cause WebView reload but better than nothing)
        log.warn("API", "Falling back to window.location.href");
        window.location.href = url;
      }
    } else {
      // Web: Use standard redirect
      log.debug("API", "Redirecting to Google OAuth URL (web):", url);
      window.location.href = url;
    }
  } catch (error) {
    log.error("API", "Failed to initiate Google connect:", error);
    throw error;
  }
};

/**
 * Fetches user profile from the backend.
 * 
 * ⚠️ CRITICAL: This is the SINGLE SOURCE OF TRUTH for athlete profile data.
 * 
 * - This endpoint (/me/profile) contains: name, weight, height, gender, location, etc.
 * - The /me endpoint contains ONLY identity: id, email, role, timezone, etc.
 * 
 * NEVER use /me to hydrate profile form fields. Always use this function.
 * 
 * IMPORTANT: This endpoint is OPTIONAL. If it fails, returns null.
 * The app should redirect to onboarding if profile is missing.
 * 
 * @returns Profile data including onboarding status and source information, or null if not available
 * 
 * Note: Backend now supports target_event and goals fields.
 * Response includes these fields if set.
 */
export const fetchUserProfile = async (): Promise<AthleteProfileOut | null> => {
  log.debug("API", "Fetching user profile (optional)");
  try {
    const response = await settingsApi.getProfile();
    const responseData = response.data || response;
    
    // Validate response is not undefined/null
    if (!responseData || typeof responseData !== 'object') {
      log.warn("API", "/me/profile returned invalid response:", responseData);
      return null;
    }
    
    // Backend now supports target_event and goals fields
    return responseData as unknown as AthleteProfileOut;
  } catch (error) {
    // Don't log CORS errors repeatedly - they're already handled by interceptor
    if (!isCorsError(error)) {
      log.warn("API", "Failed to fetch profile (this is optional):", error);
      
      // Check for database errors and provide helpful context
      if (error && typeof error === 'object' && 'status' in error) {
        const apiError = error as { status?: number; message?: string };
        
        // 500 errors mean backend is broken - return null
        if (apiError.status === 500) {
          const errorStr = (apiError.message || '').toLowerCase();
          if (errorStr.includes('column') && errorStr.includes('does not exist') ||
              errorStr.includes('programmingerror') ||
              errorStr.includes('database')) {
            log.warn("API", "Database schema error detected. Profile endpoint unavailable.");
          }
          log.warn("API", "/me/profile returned 500 - treating as optional and returning null");
          return null;
        }
        
        // 404 means profile doesn't exist yet - this is expected for new users
        if (apiError.status === 404) {
          log.debug("API", "Profile not found (404) - user needs to complete onboarding");
          return null;
        }
        
        // 401 means not authenticated - return null (auth will be handled elsewhere)
        if (apiError.status === 401) {
          log.debug("API", "Not authenticated (401) - profile unavailable");
          return null;
        }
      }
    }
    
    // For any other error, return null (treat as optional)
    return null;
  }
};

/**
 * Updates user profile on the backend.
 * @param profileData - Profile data to update (will be mapped to backend format)
 * @returns Updated profile data
 */
export const updateUserProfile = async (
  profileData: Partial<AthleteProfileOut>
): Promise<AthleteProfileOut> => {
  log.debug("API", "Updating user profile");
  try {
    // Map frontend fields to backend format
    // Note: profileData may use camelCase (frontend) or snake_case (backend)
    // We need to handle both formats
    const backendData: Record<string, unknown> = {};
    
    // Handle both camelCase and snake_case field names
    // CRITICAL: Backend expects full_name, not name
    // CRITICAL: Always send name/full_name, even if null (to clear the field)
    const nameValue = (profileData as { name?: string | null; full_name?: string | null }).name 
      ?? (profileData as { name?: string | null; full_name?: string | null }).full_name;
    // Send null explicitly if name is empty/null to clear the field
    backendData.full_name = nameValue !== undefined ? nameValue : null;
    
    // CRITICAL: Always send these fields, even if null/undefined
    // This ensures the backend knows what to update
    if (profileData.email !== undefined) backendData.email = profileData.email;
    backendData.gender = profileData.gender !== undefined ? profileData.gender : null;
    
    // Handle date_of_birth (snake_case) or dateOfBirth (camelCase)
    const dateOfBirth = (profileData as { date_of_birth?: string; dateOfBirth?: string }).date_of_birth 
      || (profileData as { date_of_birth?: string; dateOfBirth?: string }).dateOfBirth;
    if (dateOfBirth !== undefined) backendData.date_of_birth = dateOfBirth;
    
    // Handle unit_system (snake_case) or unitSystem (camelCase) - need to check this first for weight/height handling
    const unitSystem = (profileData as { unit_system?: 'imperial' | 'metric'; unitSystem?: 'imperial' | 'metric' }).unit_system 
      || (profileData as { unit_system?: 'imperial' | 'metric'; unitSystem?: 'imperial' | 'metric' }).unitSystem;
    if (unitSystem !== undefined) backendData.unit_system = unitSystem;
    
    // Handle weight - support weight_lbs (imperial) and weight_kg (metric) with 1 decimal precision
    // CRITICAL: Always send weight fields explicitly, even if null (to clear them)
    const weightLbs = (profileData as { weight_lbs?: number | null }).weight_lbs;
    const weightKg = (profileData as { weight_kg?: number | null }).weight_kg;
    
    if (weightLbs !== undefined) {
      // Send weight_lbs (null to clear, or number to set)
      backendData.weight_lbs = weightLbs !== null ? Number(weightLbs.toFixed(1)) : null;
      // Clear weight_kg when using imperial
      if (weightLbs !== null) {
        backendData.weight_kg = null;
      }
    } else if (weightKg !== undefined) {
      // Send weight_kg (null to clear, or number to set)
      backendData.weight_kg = weightKg !== null ? Number(weightKg.toFixed(1)) : null;
      // Clear weight_lbs when using metric
      if (weightKg !== null) {
        backendData.weight_lbs = null;
      }
    } else {
      // Fallback: try to get weight and convert based on unit system
      const weight = (profileData as { weight?: number | string }).weight;
      if (weight !== undefined && weight !== null) {
        const weightValue = typeof weight === 'number' ? weight : parseFloat(String(weight));
        if (!isNaN(weightValue)) {
          if (unitSystem === 'imperial') {
            backendData.weight_lbs = Number(weightValue.toFixed(1));
            backendData.weight_kg = null;
          } else {
            backendData.weight_kg = Number(weightValue.toFixed(1));
            backendData.weight_lbs = null;
          }
        }
      } else {
        // Explicitly clear both if weight is null/empty
        backendData.weight_lbs = null;
        backendData.weight_kg = null;
      }
    }
    
    // Handle height - support height_inches (imperial, integer) and height_cm (metric, integer)
    // CRITICAL: Backend expects height_inches (integer), not height_in (float)
    // CRITICAL: Always send height fields explicitly, even if null (to clear them)
    const heightInches = (profileData as { height_inches?: number | null }).height_inches;
    const heightIn = (profileData as { height_in?: number | null }).height_in;
    const heightCm = (profileData as { height_cm?: number | null }).height_cm;
    
    if (heightInches !== undefined) {
      backendData.height_inches = heightInches !== null ? Math.round(heightInches) : null;
      backendData.height_cm = null;
    } else if (heightIn !== undefined) {
      backendData.height_inches = heightIn !== null ? Math.round(heightIn) : null;
      backendData.height_cm = null;
    } else if (heightCm !== undefined) {
      backendData.height_cm = heightCm !== null ? Math.round(heightCm) : null;
      backendData.height_inches = null;
    } else {
      // Fallback: try to get height and convert based on unit system
      const height = (profileData as { height?: number | string }).height;
      if (height !== undefined && height !== null) {
        const heightValue = typeof height === 'number' ? height : parseFloat(String(height));
        if (!isNaN(heightValue)) {
          if (unitSystem === 'imperial') {
            // Convert to total inches (integer)
            backendData.height_inches = Math.round(heightValue);
            backendData.height_cm = null;
          } else {
            // Convert to cm (integer)
            backendData.height_cm = Math.round(heightValue);
            backendData.height_inches = null;
          }
        }
      } else {
        // Explicitly clear both if height is null/empty
        backendData.height_inches = null;
        backendData.height_cm = null;
      }
    }
    
    // CRITICAL: Always send location, even if null (to clear the field)
    backendData.location = profileData.location !== undefined ? profileData.location : null;
    
    // Backend now supports target_event and goals
    const targetEvent = (profileData as { target_event?: unknown; targetEvent?: unknown }).target_event 
      || (profileData as { target_event?: unknown; targetEvent?: unknown }).targetEvent;
    if (targetEvent !== undefined) {
      backendData.target_event = targetEvent;
    }
    if (profileData.goals !== undefined) {
      backendData.goals = profileData.goals;
    }
    
    // FE-4: Support race_input with source marker for backend LLM processing
    const raceInput = (profileData as { race_input?: unknown }).race_input;
    if (raceInput !== undefined) {
      backendData.race_input = raceInput;
    }

    // Log what we're sending to the backend
    log.debug("API", "Sending profile update to backend:", {
      full_name: backendData.full_name,
      location: backendData.location,
      gender: backendData.gender,
      unit_system: backendData.unit_system,
      date_of_birth: backendData.date_of_birth,
      weight_lbs: backendData.weight_lbs,
      weight_kg: backendData.weight_kg,
      height_inches: backendData.height_inches,
      height_cm: backendData.height_cm,
      fullPayload: backendData,
    });
    
    const response = await settingsApi.updateProfile(backendData);
    const responseData = response.data || response;
    
    // Log what we received from the backend
    log.debug("API", "Received profile update response:", {
      full_name: (responseData as { full_name?: string | null }).full_name,
      location: (responseData as { location?: string | null }).location,
      gender: (responseData as { gender?: string | null }).gender,
      rawResponse: responseData,
    });
    
    return responseData as unknown as AthleteProfileOut;
  } catch (error) {
    log.error("API", "Failed to update profile:", error);
    
    // Check if error is related to missing database column
    if (error && typeof error === 'object' && 'message' in error) {
      const errorMessage = String(error.message);
      if (errorMessage.includes('years_training') || errorMessage.includes('UndefinedColumn')) {
        log.warn("API", "Backend database schema issue detected - years_training column missing. This is a backend issue that needs to be fixed.");
      }
    }
    
    throw error;
  }
};

/**
 * Fetches training preferences from the backend.
 */
export const fetchTrainingPreferences = async (): Promise<{
  years_of_training: number;
  primary_sports: string[];
  available_days: string[];
  weekly_hours: number;
  training_focus: 'race_focused' | 'general_fitness';
  injury_history: boolean;
  injury_notes: string | null;
  consistency: string | null;
  philosophy: string | null;
  goal: string | null;
  ftp_watts: number | null;
  cycling_goal: string | null;
  cycling_weekly_hours: number | null;
  running_weekly_hours: number | null;
  run_threshold_pace_min_per_km: number | null;
  // CrossFit
  crossfit_experience: string | null;
  crossfit_wod_preference: string | null;
  crossfit_weekly_hours: number | null;
  // Strength
  strength_goal: string | null;
  squat_1rm_kg: number | null;
  deadlift_1rm_kg: number | null;
  bench_1rm_kg: number | null;
  // Hyrox
  hyrox_category: string | null;
  hyrox_weak_stations: string[] | null;
  hyrox_target_finish_minutes: number | null;
}> => {
  log.debug("API", "Fetching training preferences");
  try {
    const response = await settingsApi.getTrainingPreferences();
    const responseData = response.data || response;
    return responseData as unknown as {
      years_of_training: number;
      primary_sports: string[];
      available_days: string[];
      weekly_hours: number;
      training_focus: 'race_focused' | 'general_fitness';
      injury_history: boolean;
      injury_notes: string | null;
      consistency: string | null;
      philosophy: string | null;
      goal: string | null;
      ftp_watts: number | null;
      cycling_goal: string | null;
      cycling_weekly_hours: number | null;
      running_weekly_hours: number | null;
      run_threshold_pace_min_per_km: number | null;
      // CrossFit
      crossfit_experience: string | null;
      crossfit_wod_preference: string | null;
      crossfit_weekly_hours: number | null;
      // Strength
      strength_goal: string | null;
      squat_1rm_kg: number | null;
      deadlift_1rm_kg: number | null;
      bench_1rm_kg: number | null;
      // Hyrox
      hyrox_category: string | null;
      hyrox_weak_stations: string[] | null;
      hyrox_target_finish_minutes: number | null;
    };
  } catch (error) {
    log.error("API", "Failed to fetch training preferences:", error);
    throw error;
  }
};

/**
 * Completes the onboarding process. This endpoint:
 * 1. Persists all onboarding data to users, athlete_profiles, and user_settings
 * 2. Sets onboarding_complete flag
 * 3. Conditionally generates initial training plans (if user opted in and has data)
 * 
 * @param data - Onboarding data with all required fields
 * @returns Onboarding result with optional plans
 * 
 * Reference: See backend docs POST /api/onboarding/complete for complete details
 */
export const completeOnboarding = async (data: {
  role: 'athlete' | 'coach';
  first_name: string;
  last_name: string | null;
  timezone: string;
  primary_sport: 'run' | 'bike' | 'tri';
  goal_type: 'performance' | 'completion' | 'general';
  experience_level: 'beginner' | 'structured' | 'competitive';
  availability_days_per_week: number;
  availability_hours_per_week: number;
  injury_status: 'none' | 'managing' | 'injured';
  injury_notes: string | null;
  generate_initial_plan: boolean;
}): Promise<{
  status: 'ok';
  weekly_intent: unknown;
  season_plan: unknown;
  provisional: boolean;
  warning: string | null;
}> => {
  log.debug("API", "Completing onboarding");
  try {
    const response = await settingsApi.completeOnboarding(data);
    const responseData = response.data || response;
    return responseData as unknown as {
      status: 'ok';
      weekly_intent: unknown;
      season_plan: unknown;
      provisional: boolean;
      warning: string | null;
    };
  } catch (error) {
    log.error("API", "Failed to complete onboarding:", error);
    throw error;
  }
};

/**
 * Quick onboarding v2 — 3 questions, always generates a provisional plan.
 *
 * @param data - Minimal onboarding data (sport, goal, days)
 * @returns Plan data with sessions
 */
export const quickCompleteOnboarding = async (data: {
  primary_sport: 'run' | 'bike' | 'tri';
  goal_type: 'performance' | 'completion' | 'general';
  availability_days_per_week: number;
  role?: 'athlete' | 'coach';
  first_name?: string | null;
  last_name?: string | null;
  timezone?: string | null;
  race_name?: string | null;
  race_date?: string | null;
  ai_data_consent?: boolean;
}): Promise<{
  status: 'ok';
  weekly_intent: unknown;
  season_plan: unknown;
  provisional: boolean;
  warning: string | null;
  sessions_count: number;
}> => {
  log.debug("API", "Quick onboarding v2");
  try {
    const response = await settingsApi.quickCompleteOnboarding(data);
    const responseData = response.data || response;
    return responseData as unknown as {
      status: 'ok';
      weekly_intent: unknown;
      season_plan: unknown;
      provisional: boolean;
      warning: string | null;
      sessions_count: number;
    };
  } catch (error) {
    log.error("API", "Failed to quick complete onboarding:", error);
    throw error;
  }
};

/**
 * Updates training preferences on the backend.
 */
export const updateTrainingPreferences = async (
  preferences: {
    years_of_training?: number;
    primary_sports?: string[];
    available_days?: string[];
    weekly_hours?: number;
    training_focus?: 'race_focused' | 'general_fitness';
    injury_history?: boolean;
    injury_notes?: string | null;
    consistency?: string | null;
    philosophy?: string | null;
    goal?: string | null;
    ftp_watts?: number | null;
    cycling_goal?: string | null;
    cycling_weekly_hours?: number | null;
    running_weekly_hours?: number | null;
    run_threshold_pace_min_per_km?: number | null;
    // CrossFit
    crossfit_experience?: string | null;
    crossfit_wod_preference?: string | null;
    crossfit_weekly_hours?: number | null;
    // Strength
    strength_goal?: string | null;
    squat_1rm_kg?: number | null;
    deadlift_1rm_kg?: number | null;
    bench_1rm_kg?: number | null;
    // Hyrox
    hyrox_category?: string | null;
    hyrox_weak_stations?: string[] | null;
    hyrox_target_finish_minutes?: number | null;
  }
): Promise<{
  years_of_training: number;
  primary_sports: string[];
  available_days: string[];
  weekly_hours: number;
  training_focus: 'race_focused' | 'general_fitness';
  injury_history: boolean;
  injury_notes: string | null;
  consistency: string | null;
  philosophy: string | null;
  goal: string | null;
  ftp_watts: number | null;
  cycling_goal: string | null;
  cycling_weekly_hours: number | null;
  running_weekly_hours: number | null;
  run_threshold_pace_min_per_km: number | null;
  // CrossFit
  crossfit_experience: string | null;
  crossfit_wod_preference: string | null;
  crossfit_weekly_hours: number | null;
  // Strength
  strength_goal: string | null;
  squat_1rm_kg: number | null;
  deadlift_1rm_kg: number | null;
  bench_1rm_kg: number | null;
  // Hyrox
  hyrox_category: string | null;
  hyrox_weak_stations: string[] | null;
  hyrox_target_finish_minutes: number | null;
}> => {
  log.debug("API", "PUT /me/training-preferences sending", {
    available_days: preferences.available_days ?? null,
    weekly_hours: preferences.weekly_hours ?? null,
    ftp_watts: preferences.ftp_watts ?? null,
    running_weekly_hours: preferences.running_weekly_hours ?? null,
  });
  try {
    const backendPayload: Record<string, unknown> = {
      years_of_training: preferences.years_of_training ?? null,
      primary_sports: preferences.primary_sports ?? null,
      available_days: preferences.available_days ?? null,
      weekly_hours: preferences.weekly_hours ?? null,
      training_focus: preferences.training_focus ?? null,
      injury_history: preferences.injury_history ?? null,
      injury_notes: preferences.injury_notes ?? null,
      consistency: preferences.consistency ?? null,
      philosophy: preferences.philosophy ?? null,
      goal: preferences.goal ?? null,
      ftp_watts: preferences.ftp_watts ?? null,
      cycling_goal: preferences.cycling_goal ?? null,
      cycling_weekly_hours: preferences.cycling_weekly_hours ?? null,
      running_weekly_hours: preferences.running_weekly_hours ?? null,
      run_threshold_pace_min_per_km: preferences.run_threshold_pace_min_per_km ?? null,
      // CrossFit
      crossfit_experience: preferences.crossfit_experience ?? null,
      crossfit_wod_preference: preferences.crossfit_wod_preference ?? null,
      crossfit_weekly_hours: preferences.crossfit_weekly_hours ?? null,
      // Strength
      strength_goal: preferences.strength_goal ?? null,
      squat_1rm_kg: preferences.squat_1rm_kg ?? null,
      deadlift_1rm_kg: preferences.deadlift_1rm_kg ?? null,
      bench_1rm_kg: preferences.bench_1rm_kg ?? null,
      // Hyrox
      hyrox_category: preferences.hyrox_category ?? null,
      hyrox_weak_stations: preferences.hyrox_weak_stations ?? null,
      hyrox_target_finish_minutes: preferences.hyrox_target_finish_minutes ?? null,
    };

    const response = await settingsApi.updateTrainingPreferences(backendPayload);
    const responseData = response.data || response;
    log.debug("API", "PUT /me/training-preferences OK", { available_days: (responseData as { available_days?: string[] }).available_days });
    return responseData as unknown as {
      years_of_training: number;
      primary_sports: string[];
      available_days: string[];
      weekly_hours: number;
      training_focus: 'race_focused' | 'general_fitness';
      injury_history: boolean;
      injury_notes: string | null;
      consistency: string | null;
      philosophy: string | null;
      goal: string | null;
      ftp_watts: number | null;
      cycling_goal: string | null;
      cycling_weekly_hours: number | null;
      running_weekly_hours: number | null;
      run_threshold_pace_min_per_km: number | null;
      // CrossFit
      crossfit_experience: string | null;
      crossfit_wod_preference: string | null;
      crossfit_weekly_hours: number | null;
      // Strength
      strength_goal: string | null;
      squat_1rm_kg: number | null;
      deadlift_1rm_kg: number | null;
      bench_1rm_kg: number | null;
      // Hyrox
      hyrox_category: string | null;
      hyrox_weak_stations: string[] | null;
      hyrox_target_finish_minutes: number | null;
    };
  } catch (error) {
    log.error("API", "Failed to update training preferences:", error);
    throw error;
  }
};

// ============ Activity Upload & Manual Entry ============

export interface ActivityUploadResponse {
  status: 'ok';
  activity_id: string;
  deduplicated: boolean;
}

export interface ManualActivityRequest {
  date: string;
  time?: string | null;
  sport: string;
  title?: string | null;
  duration_minutes: number;
  distance_km?: number | null;
  elevation_gain_m?: number | null;
  avg_heart_rate?: number | null;
  notes?: string | null;
  perceived_effort?: number | null;
}

export interface ManualActivityResponse {
  status: string;
  activity_id: string;
}

/**
 * Uploads an activity file (FIT, GPX, TCX).
 * @param file - The file to upload
 * @returns Upload response with activity_id and deduplicated flag
 */
export const uploadActivityFile = async (file: File): Promise<ActivityUploadResponse> => {
  log.debug("API", "Uploading activity file:", file.name);

  const formData = new FormData();
  formData.append('file', file);

  try {
    // Note: Do not set Content-Type header manually for FormData
    // Axios automatically sets it with the correct boundary parameter
    const response = await activitiesApi.upload(formData);

    return response as unknown as ActivityUploadResponse;
  } catch (error) {
    log.error("API", "Failed to upload activity:", error);
    throw error;
  }
}

/**
 * Creates a completed activity from manual user input.
 */
export const createManualActivity = async (data: ManualActivityRequest): Promise<ManualActivityResponse> => {
  log.debug("API", "Creating manual activity:", data.date, data.sport);
  const response = await api.post("/activities/manual", data);
  log.debug("API", "Manual activity created:", response.data);
  return response.data;
}

/**
 * Fetches privacy settings from the backend.
 */
export const fetchPrivacySettings = async (): Promise<{
  profile_visibility: 'public' | 'private' | 'coaches';
  share_activity_data: boolean;
  share_training_metrics: boolean;
}> => {
  log.debug("API", "Fetching privacy settings");
  try {
    const response = await settingsApi.getPrivacySettings();
    const responseData = response.data || response;
    return responseData as unknown as {
      profile_visibility: 'public' | 'private' | 'coaches';
      share_activity_data: boolean;
      share_training_metrics: boolean;
    };
  } catch (error) {
    log.error("API", "Failed to fetch privacy settings:", error);
    throw error;
  }
};

/**
 * Updates privacy settings on the backend.
 */
export const updatePrivacySettings = async (
  settings: {
    profile_visibility?: 'public' | 'private' | 'coaches';
    share_activity_data?: boolean;
    share_training_metrics?: boolean;
  }
): Promise<{
  profile_visibility: 'public' | 'private' | 'coaches';
  share_activity_data: boolean;
  share_training_metrics: boolean;
}> => {
  log.debug("API", "Updating privacy settings");
  try {
    const response = await settingsApi.updatePrivacySettings(settings);
    const responseData = response.data || response;
    return responseData as unknown as {
      profile_visibility: 'public' | 'private' | 'coaches';
      share_activity_data: boolean;
      share_training_metrics: boolean;
    };
  } catch (error) {
    log.error("API", "Failed to update privacy settings:", error);
    throw error;
  }
};

/**
 * Fetches notification preferences from the backend.
 */
export const fetchNotificationPreferences = async (): Promise<{
  email_notifications: boolean;
  push_notifications: boolean;
  workout_reminders: boolean;
  training_load_alerts: boolean;
  race_reminders: boolean;
  weekly_summary: boolean;
  goal_achievements: boolean;
  coach_messages: boolean;
}> => {
  log.debug("API", "Fetching notification preferences");
  try {
    const response = await settingsApi.getNotificationPreferences();
    const responseData = response.data || response;
    return responseData as unknown as {
      email_notifications: boolean;
      push_notifications: boolean;
      workout_reminders: boolean;
      training_load_alerts: boolean;
      race_reminders: boolean;
      weekly_summary: boolean;
      goal_achievements: boolean;
      coach_messages: boolean;
    };
  } catch (error) {
    log.error("API", "Failed to fetch notification preferences:", error);
    throw error;
  }
};

/**
 * Fetches settings profile (email, name, role) from the backend.
 */
export const fetchSettingsProfile = async (): Promise<{
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'athlete' | 'coach';
}> => {
  log.debug("API", "Fetching settings profile");
  try {
    const response = await api.get("/settings/profile");
    return response as unknown as {
      email: string;
      first_name: string | null;
      last_name: string | null;
      role: 'athlete' | 'coach';
    };
  } catch (error) {
    log.error("API", "Failed to fetch settings profile:", error);
    throw error;
  }
};

/**
 * Updates settings profile (name, role) on the backend.
 */
export const updateSettingsProfile = async (
  profile: {
    first_name?: string | null;
    last_name?: string | null;
    role?: 'athlete' | 'coach';
  }
): Promise<{
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'athlete' | 'coach';
}> => {
  log.debug("API", "Updating settings profile");
  try {
    const response = await api.patch("/settings/profile", profile);
    return response as unknown as {
      email: string;
      first_name: string | null;
      last_name: string | null;
      role: 'athlete' | 'coach';
    };
  } catch (error) {
    log.error("API", "Failed to update settings profile:", error);
    throw error;
  }
};

/**
 * Updates notification preferences on the backend.
 */
export const updateNotificationPreferences = async (
  preferences: {
    email_notifications?: boolean;
    push_notifications?: boolean;
    workout_reminders?: boolean;
    training_load_alerts?: boolean;
    race_reminders?: boolean;
    weekly_summary?: boolean;
    goal_achievements?: boolean;
    coach_messages?: boolean;
  }
): Promise<{
  email_notifications: boolean;
  push_notifications: boolean;
  workout_reminders: boolean;
  training_load_alerts: boolean;
  race_reminders: boolean;
  weekly_summary: boolean;
  goal_achievements: boolean;
  coach_messages: boolean;
}> => {
  log.debug("API", "Updating notification preferences");
  try {
    const response = await settingsApi.updateNotificationPreferences(preferences);
    const responseData = response.data || response;
    return responseData as unknown as {
      email_notifications: boolean;
      push_notifications: boolean;
      workout_reminders: boolean;
      training_load_alerts: boolean;
      race_reminders: boolean;
      weekly_summary: boolean;
      goal_achievements: boolean;
      coach_messages: boolean;
    };
  } catch (error) {
    log.error("API", "Failed to update notification preferences:", error);
    throw error;
  }
};

/**
 * Changes user password.
 */
export const changePassword = async (
  passwordData: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }
): Promise<{ success: boolean; message: string }> => {
  log.debug("API", "Changing password");
  try {
    await settingsApi.changePassword(passwordData);
    return { success: true, message: "Password changed successfully" };
  } catch (error) {
    log.error("API", "Failed to change password:", error);
    throw error;
  }
};

/**
 * Changes user email.
 */
export const changeEmail = async (
  emailData: {
    new_email: string;
    password: string;
  }
): Promise<{ success: boolean; message: string }> => {
  log.debug("API", "Changing email");
  try {
    await authApi.changeEmail(emailData.new_email);
    return { success: true, message: "Email changed successfully" };
  } catch (error) {
    log.error("API", "Failed to change email:", error);
    throw error;
  }
};

/**
 * Gets integration status for all providers (Strava, Garmin).
 */
export const getIntegrationsStatus = async (): Promise<{
  integrations: Array<{
    provider: string;
    connected: boolean;
    last_sync_at: string | null;
  }>;
}> => {
  log.debug("API", "Fetching integrations status");
  try {
    const response = await api.get("/integrations/status");
    return response as unknown as {
      integrations: Array<{
        provider: string;
        connected: boolean;
        last_sync_at: string | null;
      }>;
    };
  } catch (error) {
    log.error("API", "Failed to fetch integrations status:", error);
    throw error;
  }
};

/**
 * Initiates Garmin OAuth connection.
 * Redirects to Garmin OAuth flow.
 */
export const initiateGarminConnect = async (): Promise<void> => {
  log.debug("API", "Initiating Garmin connect");
  
  try {
    // Check if running in native app (Capacitor)
    const { isNative } = await import('./platform');
    const isCapacitor = isNative();
    
    // Get base URL from API config or use window location
    const baseURL = (api.defaults.baseURL as string) || window.location.origin;
    const oauthUrl = `${baseURL}/integrations/garmin/connect`;
    
    if (isCapacitor) {
      const { Browser } = await import('@capacitor/browser');
      log.debug("API", "Opening Garmin OAuth in external browser (native)");
      try {
        await Browser.open({ 
          url: oauthUrl,
          presentationStyle: 'popover'
        });
      } catch (error) {
        log.error("API", "Browser.open() failed:", error);
        window.location.href = oauthUrl;
      }
    } else {
      // Web: Use standard redirect
      log.debug("API", "Redirecting to Garmin OAuth URL (web)");
      window.location.href = oauthUrl;
    }
  } catch (error) {
    log.error("API", "Failed to initiate Garmin connect:", error);
    
    // Check for EMAIL_REQUIRED error
    const apiError = error as { status?: number; response?: { data?: { detail?: string } }; headers?: { 'x-error-code'?: string } };
    if (apiError.status === 403 && (apiError.headers?.['x-error-code'] === 'EMAIL_REQUIRED' || apiError.response?.data?.detail?.includes('Email'))) {
      throw new Error('EMAIL_REQUIRED');
    }
    
    throw error;
  }
};

/**
 * Garmin status response: connected, status, last_webhook_at, cursors.
 */
export interface GarminStatusResponse {
  connected: boolean;
  status: string | null;
  /** Garmin User ID (from OAuth) – confirms we received it. */
  garmin_user_id?: string | null;
  /** True when access_token and refresh_token are stored (encrypted). */
  tokens_stored?: boolean;
  last_webhook_at: string | null;
  last_error: string | null;
  message: string | null;
  cursors: Record<string, { last_successful_start_time_s: number | null; last_successful_end_time_s: number | null }>;
}

/**
 * Fetches Garmin integration status (connected, status, last_webhook_at, cursors).
 */
export const getGarminStatus = async (): Promise<GarminStatusResponse> => {
  const { data } = await api.get<GarminStatusResponse>("/integrations/garmin/status");
  return data;
};

/**
 * Disconnects Garmin integration.
 */
export const disconnectGarmin = async (): Promise<void> => {
  log.debug("API", "Disconnecting Garmin");
  try {
    await api.delete("/integrations/garmin");
    log.debug("API", "Garmin disconnected successfully");
  } catch (error) {
    const apiError = error as { status?: number };
    // Only throw errors for status >= 500
    if (apiError.status && apiError.status >= 500) {
      log.error("API", "Failed to disconnect Garmin (server error):", error);
      throw error;
    }
    // For 4xx errors, treat as success (already disconnected)
    log.debug("API", "Garmin already disconnected or not found");
  }
};

// ---------------------------------------------------------------------------
// Apple Health
// ---------------------------------------------------------------------------

export interface AppleHealthSyncPayload {
  id: string;
  sport: string;
  start_date: string;
  end_date: string;
  duration_seconds: number;
  distance_meters?: number | null;
  calories?: number | null;
  avg_heart_rate?: number | null;
  max_heart_rate?: number | null;
  source_name?: string | null;
  source_bundle_id?: string | null;
}

export interface AppleHealthSyncResult {
  imported: number;
  skipped: number;
  errors: number;
}

/** POST /integrations/apple-health/sync — send HealthKit workouts to backend. */
export const syncAppleHealthActivities = async (
  activities: AppleHealthSyncPayload[]
): Promise<AppleHealthSyncResult> => {
  log.debug("API", "Syncing Apple Health activities:", activities.length);
  const response = await api.post("/integrations/apple-health/sync", {
    activities,
  });
  return response as unknown as AppleHealthSyncResult;
};

/** POST /integrations/apple-health/status — set connection status. */
export const setAppleHealthConnected = async (
  connected: boolean
): Promise<void> => {
  log.debug("API", "Setting Apple Health connected:", connected);
  await api.post("/integrations/apple-health/status", { connected });
};

/**
 * Triggers Garmin summary backfill. Activities will arrive via webhooks.
 */
export const triggerGarminHistoryBackfill = async (): Promise<{ status: string; message: string; user_id: string }> => {
  const { data } = await api.post<{ status: string; message: string; user_id: string }>("/integrations/garmin/history-backfill");
  return data;
};

/**
 * Normalizes activity timestamps from API response to ensure proper UTC handling.
 * This function should be applied immediately when activities are received from the API.
 */
function normalizeActivityDate(dateField: unknown): string {
  if (!dateField) {
    return '';
  }
  
  let date: Date;
  if (typeof dateField === 'string') {
    // Normalize timezone offsets: +00:00 -> Z, -00:00 -> Z
    let normalizedStr = dateField.replace(/\+00:00$/, 'Z').replace(/-00:00$/, 'Z');
    // If string doesn't end with Z, assume UTC and append Z
    normalizedStr = normalizedStr.endsWith('Z') ? normalizedStr : normalizedStr + 'Z';
    date = new Date(normalizedStr);
  } else if (dateField instanceof Date) {
    date = dateField;
  } else {
    // Try to parse as string
    const str = String(dateField);
    // Normalize timezone offsets: +00:00 -> Z, -00:00 -> Z
    let normalizedStr = str.replace(/\+00:00$/, 'Z').replace(/-00:00$/, 'Z');
    normalizedStr = normalizedStr.endsWith('Z') ? normalizedStr : normalizedStr + 'Z';
    date = new Date(normalizedStr);
  }
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    log.warn("API", 'Invalid date field:', dateField);
    return typeof dateField === 'string' ? dateField.split('T')[0] : '';
  }
  
  // Return normalized date string in YYYY-MM-DD format (UTC)
  return date.toISOString().split('T')[0];
}

function _normalizeClimateExpectation(raw: unknown): ClimateExpectation | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'object' || !('primary' in raw)) return null;
  const obj = raw as { primary?: unknown; detail?: unknown };
  if (typeof obj.primary !== 'string') return null;
  const detail =
    obj.detail != null && typeof obj.detail === 'string' ? obj.detail : null;
  return { primary: obj.primary, detail };
}

function _normalizeActivitySource(raw: unknown): 'strava' | 'garmin' | 'apple_health' | 'manual' {
  const s = raw != null && typeof raw === 'string' ? raw.toLowerCase() : '';
  if (s === 'strava') return 'strava';
  if (s === 'garmin') return 'garmin';
  if (s === 'apple_health') return 'apple_health';
  return 'manual';
}

const _toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Fetches activities from the backend.
 * Note: Backend has a maximum limit of 100. Larger limits will be capped at 100.
 */
export const fetchActivities = async (params?: { limit?: number; offset?: number; start?: string; end?: string }): Promise<import("../types").CompletedActivity[]> => {
  log.debug("API", "Fetching activities with params:", params);
  try {
    // Ensure limit doesn't exceed backend maximum of 1000 (increased from 100)
    const safeParams = params ? {
      ...params,
      limit: params.limit && params.limit > 1000 ? 1000 : params.limit,
    } : params;
    // Use longer timeout for activities list (cold DB / large total count can be slow)
    const response = await activitiesApi.list(safeParams, { timeout: 90000 });
    log.debug("API", "Activities response:", response);
    
    // Handle axios response structure (response.data) or direct response
    const responseData = response.data || response;
    
    let activitiesArray: unknown[] = [];
    
    // Handle different response formats
    if (Array.isArray(responseData)) {
      activitiesArray = responseData;
    } else if (responseData && typeof responseData === "object") {
      // If response is an object, try to extract the array
      const activities = (responseData as { activities?: unknown[]; data?: unknown[]; items?: unknown[] }).activities 
        || (responseData as { activities?: unknown[]; data?: unknown[]; items?: unknown[] }).data
        || (responseData as { activities?: unknown[]; data?: unknown[]; items?: unknown[] }).items;
      
      if (Array.isArray(activities)) {
        activitiesArray = activities;
      }
    }
    
    // Log first activity to see structure
    if (activitiesArray.length > 0) {
      const sample = activitiesArray[0] as Record<string, unknown>;
      log.debug("API", 'Sample activity structure:', sample);
      log.debug("API", 'Activity keys:', Object.keys(sample));
      // Log climate fields specifically
      log.debug("API", 'Climate fields in sample:', {
        heat_stress_index: sample.heat_stress_index,
        heat_acclimation_score: sample.heat_acclimation_score,
        effective_heat_stress_index: sample.effective_heat_stress_index,
        wind_chill_c: sample.wind_chill_c,
        cold_stress_index: sample.cold_stress_index,
        avg_temperature_c: sample.avg_temperature_c,
        conditions_label: sample.conditions_label,
      });
    }
    
    // First, remove duplicates by strava_activity_id (keep the first occurrence)
    const seenStravaIds = new Set<string | number>();
    let duplicateCount = 0;
    const uniqueActivities = activitiesArray.filter((activity) => {
      if (!activity || typeof activity !== 'object') {
        return false;
      }
      const act = activity as Record<string, unknown>;
      const stravaId = act.strava_activity_id;
      
      // If we've seen this Strava activity ID before, skip it
      if (stravaId !== undefined && stravaId !== null) {
        const idStr = String(stravaId);
        if (seenStravaIds.has(idStr)) {
          duplicateCount++;
          return false; // Duplicate
        }
        seenStravaIds.add(idStr);
      }
      
      return true;
    });
    
    if (duplicateCount > 0) {
      log.debug("API", `Removed ${duplicateCount} duplicate activities (by strava_activity_id)`);
    }
    
    // Map backend activity format to frontend format
    const validActivities = uniqueActivities
      .filter((activity) => {
        if (!activity || typeof activity !== 'object') {
          return false;
        }
        const act = activity as Record<string, unknown>;
        
        // Must have an ID
        if (!act.id || typeof act.id !== 'string') {
          return false;
        }
        
        // Must have a date (try multiple possible field names)
        const dateField = act.start_time || act.date || act.start_date || act.start_date_local || act.activity_date;
        if (!dateField || (typeof dateField !== 'string' && !(dateField instanceof Date))) {
          return false;
        }
        
        // Must have a sport/type (try multiple possible field names)
        const sportField = act.sport || act.type || act.activity_type || act.sport_type;
        if (!sportField || typeof sportField !== 'string') {
          return false;
        }
        
        // Filter out obvious mock/placeholder data
        const title = (act.title || act.name || act.activity_name || '').toString().toLowerCase();
        if (title.includes('placeholder') || title.includes('mock') || title.includes('sample')) {
          return false;
        }
        
        return true;
      })
      .map((activity) => {
        const act = activity as Record<string, unknown>;
        
        // Map backend fields to frontend format
        const dateField = act.start_time || act.date || act.start_date || act.start_date_local || act.activity_date || '';
        const sportField = act.type || act.sport || act.activity_type || act.sport_type || '';
        const titleField = act.title || act.name || act.activity_name || `${sportField} Activity`;
        
        // Normalize date using the normalization function (ensures UTC handling)
        const dateStr = normalizeActivityDate(dateField);
        
        // Normalize sport to match frontend types
        const normalizedSport = typeof sportField === 'string' ? sportField.toLowerCase() : String(sportField).toLowerCase();
        let sport: 'running' | 'cycling' | 'swimming' | 'triathlon' = 'running';
        if (normalizedSport.includes('run')) {
          sport = 'running';
        } else if (normalizedSport.includes('ride') || normalizedSport.includes('bike') || normalizedSport.includes('cycle')) {
          sport = 'cycling';
        } else if (normalizedSport.includes('swim')) {
          sport = 'swimming';
        } else if (normalizedSport.includes('tri')) {
          sport = 'triathlon';
        }
        
        // Map duration (could be in seconds or minutes)
        let duration = 0;
        const durationSeconds = _toNumberOrUndefined(act.duration_seconds);
        const durationRaw = _toNumberOrUndefined(act.duration);
        const movingTime = _toNumberOrUndefined(act.moving_time);
        const elapsedTime = _toNumberOrUndefined(act.elapsed_time);
        const durationMinutes = _toNumberOrUndefined(act.duration_minutes);
        if (durationSeconds !== undefined) {
          duration = Math.round(durationSeconds / 60); // Convert seconds to minutes
        } else if (durationRaw !== undefined) {
          duration = durationRaw > 1000 ? Math.round(durationRaw / 60) : Math.round(durationRaw); // If > 1000, assume seconds
        } else if (movingTime !== undefined) {
          duration = Math.round(movingTime / 60);
        } else if (elapsedTime !== undefined) {
          duration = Math.round(elapsedTime / 60);
        } else if (durationMinutes !== undefined) {
          duration = Math.round(durationMinutes);
        }
        
        // Map distance (could be in meters or km) - round to 1 decimal place
        let distance = 0;
        if (typeof act.distance_meters === 'number') {
          distance = Math.round((act.distance_meters / 1000) * 10) / 10; // Convert meters to km, round to 1 decimal
        } else if (typeof act.distance === 'number') {
          distance = act.distance > 100 
            ? Math.round((act.distance / 1000) * 10) / 10 
            : Math.round(act.distance * 10) / 10; // If > 100, assume meters
        } else if (typeof act.distance_km === 'number') {
          distance = Math.round(act.distance_km * 10) / 10;
        }
        
        // Map elevation - round to 1 decimal place
        let elevation: number | undefined = undefined;
        if (typeof act.elevation_gain_meters === 'number') {
          elevation = Math.round(act.elevation_gain_meters * 10) / 10;
        } else if (typeof act.total_elevation_gain === 'number') {
          elevation = Math.round(act.total_elevation_gain * 10) / 10;
        } else if (typeof act.elevation_gain === 'number') {
          elevation = Math.round(act.elevation_gain * 10) / 10;
        } else if (typeof act.elevation === 'number') {
          elevation = Math.round(act.elevation * 10) / 10;
        }
        
        return {
          id: act.id as string,
          date: dateStr,
          sport,
          title: typeof titleField === 'string' ? titleField : titleField.toString(),
          duration,
          distance,
          avgPace: act.average_speed ? (typeof act.average_speed === 'number' ? (() => {
            const secPerKm = 1000 / act.average_speed;
            const mins = Math.floor(secPerKm / 60);
            const secs = Math.round(secPerKm % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          })() : act.average_speed.toString()) : undefined,
          avgHeartRate: typeof act.average_heartrate === 'number' ? act.average_heartrate :
                       typeof act.avg_heart_rate === 'number' ? act.avg_heart_rate :
                       typeof act.heart_rate === 'number' ? act.heart_rate : undefined,
          avgPower: typeof act.average_watts === 'number' ? act.average_watts :
                   typeof act.avg_power === 'number' ? act.avg_power :
                   typeof act.power === 'number' ? act.power : undefined,
          elevation,
          trainingLoad: _toNumberOrUndefined(act.training_load) ??
                       _toNumberOrUndefined(act.tss) ??
                       _toNumberOrUndefined(act.stress_score) ??
                       _toNumberOrUndefined(act.load) ?? 0,
          source: _normalizeActivitySource(act.source ?? act.source_provider),
          workout_id: typeof act.workout_id === 'string' ? act.workout_id : undefined, // Link to canonical Workout (backend Phase 4+)
          planned_session_id: typeof act.planned_session_id === 'string' ? act.planned_session_id : 
                             (act.planned_session_id === null ? null : undefined), // Link to planned session (pairing field)
          coachFeedback: typeof act.coach_feedback === 'string' ? act.coach_feedback :
                        typeof act.coachFeedback === 'string' ? act.coachFeedback : undefined,
          climateExpectation: _normalizeClimateExpectation(act.climate_expectation ?? act.climateExpectation),
          normalizedPower: typeof act.normalized_power === 'number' ? act.normalized_power :
                          typeof act.normalizedPower === 'number' ? act.normalizedPower : undefined,
          intensityFactor: typeof act.intensity_factor === 'number' ? act.intensity_factor :
                          typeof act.intensityFactor === 'number' ? act.intensityFactor : undefined,
          effortSource: act.effort_source === 'power' || act.effort_source === 'pace' || act.effort_source === 'hr' ? act.effort_source :
                      act.effortSource === 'power' || act.effortSource === 'pace' || act.effortSource === 'hr' ? act.effortSource : undefined,
          // v1.1: Heat acclimation fields
          heatStressIndex: typeof act.heat_stress_index === 'number' ? act.heat_stress_index :
                          typeof act.heatStressIndex === 'number' ? act.heatStressIndex : undefined,
          heatAcclimationScore: typeof act.heat_acclimation_score === 'number' ? act.heat_acclimation_score :
                               typeof act.heatAcclimationScore === 'number' ? act.heatAcclimationScore : undefined,
          effectiveHeatStressIndex: typeof act.effective_heat_stress_index === 'number' ? act.effective_heat_stress_index :
                                   typeof act.effectiveHeatStressIndex === 'number' ? act.effectiveHeatStressIndex : undefined,
          // v2.0: Cold stress fields
          windChillC: typeof act.wind_chill_c === 'number' ? act.wind_chill_c :
                     typeof act.windChillC === 'number' ? act.windChillC : undefined,
          coldStressIndex: typeof act.cold_stress_index === 'number' ? act.cold_stress_index :
                          typeof act.coldStressIndex === 'number' ? act.coldStressIndex : undefined,
          avgTemperatureC: typeof act.avg_temperature_c === 'number' ? act.avg_temperature_c :
                          typeof act.avgTemperatureC === 'number' ? act.avgTemperatureC : undefined,
          avgDewPointC: typeof act.avg_dew_point_c === 'number' ? act.avg_dew_point_c :
                       typeof act.avgDewPointC === 'number' ? act.avgDewPointC : undefined,
          windAvgMps: typeof act.wind_avg_mps === 'number' ? act.wind_avg_mps :
                     typeof act.windAvgMps === 'number' ? act.windAvgMps : undefined,
          conditionsLabel: typeof act.conditions_label === 'string' ? act.conditions_label :
                          typeof act.conditionsLabel === 'string' ? act.conditionsLabel : undefined,
          hasStreams: act.has_streams === true,
          // Garmin brand attribution: device model (strip leading "Garmin " to prevent duplication)
          deviceModel: (() => {
            const raw = typeof act.device_model === 'string' ? act.device_model :
                        typeof act.deviceModel === 'string' ? act.deviceModel : undefined;
            return raw?.replace(/^Garmin\s+/i, '') || raw;
          })(),
          // Apple Health attribution: source app name
          sourceApp: typeof act.source_app === 'string' ? act.source_app :
                     typeof act.sourceApp === 'string' ? act.sourceApp : undefined,
        } as import("../types").CompletedActivity;
      });
    
    log.debug("API", `Filtered ${validActivities.length} valid activities from ${activitiesArray.length} total`);
    
    if (validActivities.length === 0 && activitiesArray.length > 0) {
      log.warn("API", "All activities were filtered out. Raw response:", response);
    }
    
    return validActivities;
  } catch (error) {
    log.error("API", "Failed to fetch activities:", error);
    throw error;
  }
};

/**
 * Fetches a single activity by ID.
 */
export const fetchActivity = async (id: string): Promise<import("../types").CompletedActivity> => {
  log.debug("API", "Fetching activity", id);
  try {
    const response = await activitiesApi.getById(id);
    const responseData = response.data || response;
    return responseData as unknown as import("../types").CompletedActivity;
  } catch (error) {
    log.error("API", "Failed to fetch activity:", error);
    throw error;
  }
};

/**
 * Manually pairs an activity with a planned session.
 * POST /admin/pairing/merge
 */
export const manualPair = async (activityId: string, plannedSessionId: string): Promise<void> => {
  log.debug("API", "Manually pairing activity", activityId, "with session", plannedSessionId);
  try {
    await activitiesApi.manualPair(activityId, plannedSessionId);
  } catch (error) {
    log.error("API", "Failed to manually pair activity:", error);
    throw error;
  }
};

/**
 * Manually unpairs an activity from its planned session.
 * POST /admin/pairing/unmerge
 */
export const manualUnpair = async (activityId: string): Promise<void> => {
  log.debug("API", "Manually unpairing activity", activityId);
  try {
    await activitiesApi.manualUnpair(activityId);
  } catch (error) {
    log.error("API", "Failed to manually unpair activity:", error);
    throw error;
  }
};

/**
 * Fetches activity streams (GPS, heart rate, power, etc.).
 * Follows the workflow:
 * 1. Check if streams exist via activity endpoint (has_streams field)
 * 2. If not available, POST /activities/{id}/fetch-streams to fetch from Strava
 * 3. GET /activities/{id}/streams to get formatted data (with retries)
 * 
 * Note: Stops retrying immediately on CORS errors to avoid console spam.
 */
const unwrapStreamArray = (value: unknown): unknown[] | undefined => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return undefined;

  const v = value as Record<string, unknown>;
  if (Array.isArray(v.data)) return v.data;
  if (Array.isArray(v.values)) return v.values;
  if (Array.isArray(v.items)) return v.items;

  return undefined;
};

const unwrapNumberArray = (value: unknown): number[] | undefined => {
  const arr = unwrapStreamArray(value);
  if (!arr) return undefined;
  const nums = arr
    .map((x) => (typeof x === 'number' ? x : Number(x)))
    .filter((x) => Number.isFinite(x));
  return nums;
};

const unwrapNullableNumberArray = (value: unknown): (number | null)[] | undefined => {
  const arr = unwrapStreamArray(value);
  if (!arr) return undefined;
  return arr.map((x) => {
    if (x === null || x === undefined) return null;
    const n = typeof x === 'number' ? x : Number(x);
    return Number.isFinite(n) ? n : null;
  });
};

const unwrapCoordArray = (value: unknown): number[][] | undefined => {
  const arr = unwrapStreamArray(value);
  if (!arr) return undefined;

  const coords = arr
    .map((coord) => {
      if (!Array.isArray(coord) || coord.length < 2) return null;
      const lat = typeof coord[0] === 'number' ? coord[0] : Number(coord[0]);
      const lng = typeof coord[1] === 'number' ? coord[1] : Number(coord[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return [lat, lng] as [number, number];
    })
    .filter((c): c is [number, number] => c !== null);

  return coords;
};

const normalizeActivityStreamsResponse = (raw: unknown): ActivityStreamsResponse => {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const maybeStreams = root.streams && typeof root.streams === 'object' ? (root.streams as Record<string, unknown>) : root;
  const base = maybeStreams.data && typeof maybeStreams.data === 'object' ? (maybeStreams.data as Record<string, unknown>) : maybeStreams;

  const time = unwrapNumberArray(base.time) ?? [];
  const distance = unwrapNumberArray(base.distance) ?? [];

  // Prefer route_points; fallback to Strava-style latlng.
  const route_points =
    unwrapCoordArray(base.route_points) ??
    unwrapCoordArray(base.latlng) ??
    [];

  const elevation = unwrapNumberArray(base.elevation) ?? [];
  const pace = unwrapNullableNumberArray(base.pace) ?? [];

  const heartrateArr = unwrapNumberArray(base.heartrate ?? base.heart_rate);
  const powerArr = unwrapNumberArray(base.power ?? base.watts);
  const cadenceArr = unwrapNumberArray(base.cadence);

  const data_points =
    typeof base.data_points === 'number' && Number.isFinite(base.data_points)
      ? base.data_points
      : time.length;

  return {
    time,
    route_points,
    elevation,
    pace,
    distance,
    data_points,
    ...(heartrateArr && heartrateArr.length ? { heartrate: heartrateArr } : {}),
    ...(powerArr && powerArr.length ? { power: powerArr } : {}),
    ...(cadenceArr && cadenceArr.length ? { cadence: cadenceArr } : {}),
  };
};

export const fetchActivityStreams = async (id: string): Promise<ActivityStreamsResponse> => {
  log.debug("API", "Fetching activity streams", id);

  try {
    // First, try to get the activity to check if streams are available
    let activity;
    let hasStreams = false;
    try {
      const activityResponse = await activitiesApi.getById(id);
      activity = activityResponse.data || activityResponse;
      hasStreams = (activity as { has_streams?: boolean })?.has_streams === true;
      const source = (activity as { source?: string })?.source;
      if (source === 'manual') {
        throw new Error("Detailed streams not available for this activity.");
      }
    } catch (error) {
      if (error instanceof Error && error.message === "Detailed streams not available for this activity.") {
        throw error;
      }
      if (isCorsError(error)) {
        log.warn("API", "CORS error checking activity streams. Streams feature unavailable.");
        throw new Error("Stream data is not available due to server configuration.");
      }
      log.warn("API", "Could not fetch activity to check has_streams, proceeding...", error);
    }
    
    // If streams are not available, try to fetch them (Strava only; Garmin returns 200 + status "unavailable")
    if (!hasStreams) {
      log.debug("API", "Streams not available, calling fetch-streams...");
      try {
        const fetchResponse = await activitiesApi.fetchStreams(id);
        const body = (fetchResponse.data ?? fetchResponse) as {
          success?: boolean;
          status?: 'ready' | 'ok' | 'pending' | 'unavailable';
          message?: string;
        };
        if (body.status === 'unavailable') {
          throw new Error("Detailed streams not available for this activity.");
        }
        if (body.status === 'pending') {
          throw new Error("Processing activity data… try again in a few minutes.");
        }
        // status "ready" or "ok": getStreams below (or already have data)
        if (body.status !== 'ready' && body.status !== 'ok') {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (error) {
        if (error instanceof Error && error.message.startsWith("Stream data is not available")) {
          throw error;
        }
        if (isCorsError(error)) {
          log.warn("API", "CORS error fetching streams. Streams feature unavailable.");
          throw new Error("Stream data is not available due to server configuration.");
        }
        const errorStatus = (error as { response?: { status?: number } })?.response?.status;
        if (errorStatus === 500) {
          log.error("API", "Failed to fetch streams (server error):", error);
          throw new Error("Failed to fetch stream data from Strava. Please try again later.");
        }
        if (errorStatus === 404) {
          throw new Error("Activity not found.");
        }
        log.warn("API", "Failed to fetch streams:", error);
        throw error;
      }
    }
    
    // Try to get the formatted streams data with retries
    // The backend might need time to process after fetching
    let lastError: unknown;
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds between retries
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await activitiesApi.getStreams(id);
        const responseData = response.data || response;
        return normalizeActivityStreamsResponse(responseData);
      } catch (error) {
        // Stop immediately on CORS errors - don't retry
        if (isCorsError(error)) {
          log.warn("API", "CORS error fetching streams. Streams feature unavailable.");
          throw new Error("Stream data is not available due to server configuration.");
        }
        
        lastError = error;
        const errorStatus = (error as { response?: { status?: number } })?.response?.status;
        const errorMessage = (error as { message?: string })?.message || '';
        
        // Backend now returns proper status codes:
        // 404 = Streams not available (might be processing, so retry)
        // 500 = Server error (don't retry)
        if (errorStatus === 500) {
          log.error("API", "Server error fetching streams:", error);
          throw new Error("Server error while fetching stream data. Please try again later.");
        }
        
        // If it's a 404 and we haven't exhausted retries, wait and retry
        // (streams might still be processing after fetch)
        if (errorStatus === 404 && attempt < maxRetries - 1) {
          log.debug("API", `Streams not ready yet, retrying in ${retryDelay}ms... (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }
        
        // If it's the last attempt or a different error, throw
        if (attempt === maxRetries - 1) {
          if (errorStatus === 404) {
            throw new Error("Stream data is not available for this activity.");
          }
          throw error;
        }
      }
    }
    
    // Should not reach here, but just in case
    throw lastError;
  } catch (error) {
    // Don't log CORS errors repeatedly - they're already handled
    if (!isCorsError(error)) {
      log.error("API", "Failed to fetch activity streams:", error);
    }
    throw error;
  }
};

/**
 * Fetches training load data including raw TSS, CTL, ATL, TSB.
 * 
 * @param days Optional number of days to look back (default: 60)
 */
export const fetchTrainingLoad = async (days?: number): Promise<{
  dates: string[];
  daily_load: number[];
  daily_tss: number[];
  ctl: number[];
  atl: number[];
  tsb: number[];
  ctl_raw?: number[];
  atl_raw?: number[];
  tsb_raw?: number[];
  ramp_rate_ctl_per_week?: number;
  weekly_dates?: string[];
  weekly_volume?: number[];
  weekly_rolling_avg?: number[];
  weekly_tss_dates?: string[];
  weekly_tss?: number[];
  weekly_tss_rolling_avg?: number[];
  last_updated?: string;
}> => {
  log.debug("API", "Fetching training load");
  try {
    const params = days ? { days } : undefined;
    // Use longer timeout for training load as it processes many activities
    const response = await api.get("/state/training-load", { 
      params,
      timeout: 60000, // 60 seconds - training load processing can take time with many activities
    });
    return response as unknown as {
      dates: string[];
      daily_load: number[];
      daily_tss: number[];
      ctl: number[];
      atl: number[];
      tsb: number[];
      ctl_raw?: number[];
      atl_raw?: number[];
      tsb_raw?: number[];
      ramp_rate_ctl_per_week?: number;
      weekly_dates?: string[];
      weekly_volume?: number[];
      weekly_rolling_avg?: number[];
      weekly_tss_dates?: string[];
      weekly_tss?: number[];
      weekly_tss_rolling_avg?: number[];
      last_updated?: string;
    };
  } catch (error) {
    // Return default empty response for 500 errors - training load is optional
    // This prevents retry spam and allows components to handle gracefully
    const apiError = error as { status?: number; code?: string; message?: string };
    if (apiError.status === 500 || apiError.status === 503) {
      log.warn("API", "Training load endpoint returned 500, returning empty response");
      return {
        dates: [],
        daily_load: [],
        daily_tss: [],
        ctl: [],
        atl: [],
        tsb: [],
        ctl_raw: [],
        atl_raw: [],
        tsb_raw: [],
        ramp_rate_ctl_per_week: 0,
        weekly_tss_dates: [],
        weekly_tss: [],
        weekly_tss_rolling_avg: [],
      };
    }
    // For other errors (timeouts, network, etc.), still throw to allow components to handle
    log.error("API", "Failed to fetch training load:", error);
    throw error;
  }
};

/**
 * Fetches training overview data.
 */
export const fetchOverview = async (days?: number): Promise<{
  today: { ctl: number; atl: number; tsb: number };
  metrics: {
    ctl?: [string, number][];
    atl?: [string, number][];
    tsb?: [string, number][];
    tss?: [string, number][];
  };
}> => {
  log.debug("API", "Fetching overview");
  try {
    const response = await settingsApi.getOverview(days);
    const responseData = response.data || response;
    return responseData as unknown as {
      today: { ctl: number; atl: number; tsb: number };
      metrics: {
        ctl?: [string, number][];
        atl?: [string, number][];
        tsb?: [string, number][];
        tss?: [string, number][];
      };
    };
  } catch (error) {
    log.error("API", "Failed to fetch overview:", error);
    throw error;
  }
};

/**
 * Fetches coaching summary from real LLM.
 */
export const fetchCoachSummary = async (): Promise<{
  summary: string;
  current_state: string;
  next_focus: string;
  last_updated: string;
}> => {
  log.debug("API", "Fetching coach summary");
  try {
    const response = await api.get("/coach/summary");
    return response as unknown as {
      summary: string;
      current_state: string;
      next_focus: string;
      last_updated: string;
    };
  } catch (error) {
    log.error("API", "Failed to fetch coach summary:", error);
    throw error;
  }
};

/**
 * Sends a chat message to the coach.
 */
export interface PlanItem {
  id: string;
  title: string;
  description?: string;
  date?: string;
  sport?: string;
}

export interface CoachChatResponse {
  reply?: string;
  intent?: string;
  conversation_id?: string;
  show_plan?: boolean;
  plan_items?: PlanItem[];
  planned_weeks?: Array<{
    week: number;
    weekStart: string;
    weekEnd: string;
    sessions: Array<{
      session_id: string;
      date: string;
      type: string;
      duration: number;
      distance?: number;
      template_name: string;
      notes?: string;
    }>;
    coachNotes?: string;
  }>;
  response_type?: 'plan' | 'weekly_plan' | 'season_plan' | 'session_plan' | 'recommendation' | 'summary' | 'greeting' | 'question' | 'explanation' | 'smalltalk' | 'plan_applied' | 'plan_not_applied';
  message_type?: 'assistant' | 'progress' | 'final';
  progress_stage?: string;
  progress_stages?: Array<{
    id: string;
    label: string;
    status: 'completed' | 'in_progress' | 'pending';
  }>;
  /** Proposed planning steps from backend (planner.steps or action_plan) for checklist. */
  proposed_steps?: Array<{ id: string; label: string }>;
  workout_id?: string;
  /** Structured season plan artifact returned when a season plan is generated. */
  season_plan?: {
    artifact_type?: string;
    season?: {
      start_date: string;
      end_date: string;
      goal: string;
      training_philosophy: string;
      races: Array<{ date: string; distance_or_type: string; priority: string }>;
      phases: Array<{
        phase_name: string;
        goal_description: string;
        allowed_workout_types: string[];
        volume_rule: string;
      }>;
    };
    week0?: {
      week_index: number;
      phase_name: string;
      target_volume: number;
      intensity_days: number;
      long_run_day: number;
      recovery_flag: boolean;
    };
    days0?: Array<{
      workout_type: string;
      duration_or_structure: string;
      notes: string;
    }>;
    calendar_sessions?: Array<{
      date: string;
      workout_type: string;
      duration_or_structure: string;
      notes: string;
    }>;
    athlete_summary?: string | null;
  };
}

/** Prefixes that indicate process-step (internal) items; must not be shown. Match backend filter. */
const PROCESS_STEP_PREFIXES = [
  'review ', 'consider ', 'adjust ', 'determine ', 'schedule ', 'include ',
  'retrieving ', 'providing ', 'highlighting ', 'analyzing ', 'assessing ',
];

function filterProcessStepPlanItems(items: PlanItem[]): PlanItem[] | undefined {
  const kept = items.filter((item) => {
    const t = (item.title ?? '').trim().toLowerCase();
    if (!t) return false;
    return !PROCESS_STEP_PREFIXES.some((p) => t.startsWith(p));
  });
  return kept.length > 0 ? kept : undefined;
}

/**
 * Converts string-based plan_items from orchestrator to PlanItem objects for frontend display
 * FE-2: Handles conversion from backend list[str] to frontend PlanItem[] format
 */
function mapStringPlanItemsToPlanItems(
  planItems: string[] | PlanItem[] | undefined
): PlanItem[] | undefined {
  if (!planItems || planItems.length === 0) {
    return undefined;
  }

  // Check if first item is a string (backend format) or already a PlanItem object
  const firstItem = planItems[0];
  if (typeof firstItem === 'string') {
    // Convert string[] to PlanItem[]
    return (planItems as string[]).map((item, index) => ({
      id: `plan-item-${index}`,
      title: item,
      description: undefined,
      date: undefined,
      sport: undefined,
    }));
  }

  // Already PlanItem[] objects, return as-is
  if (typeof firstItem === 'object' && firstItem !== null && 'id' in firstItem && 'title' in firstItem) {
    return planItems as PlanItem[];
  }

  // Fallback: try to convert anyway
  return undefined;
}

/**
 * Maps planned_weeks from backend to plan_items format for frontend display
 * FE-1: Converts backend planned_weeks structure to frontend plan_items format
 */
function mapPlannedWeeksToPlanItems(
  plannedWeeks?: CoachChatResponse['planned_weeks']
): PlanItem[] | undefined {
  if (!plannedWeeks || plannedWeeks.length === 0) {
    return undefined;
  }

  const planItems: PlanItem[] = [];
  
  for (const week of plannedWeeks) {
    for (const session of week.sessions) {
      // Map session type to sport (case-insensitive)
      const sessionType = session.type?.toLowerCase() || '';
      let sport: 'running' | 'cycling' | 'swimming' | 'triathlon' = 'running';
      
      if (sessionType.includes('swim')) {
        sport = 'swimming';
      } else if (sessionType.includes('bike') || sessionType.includes('cycle')) {
        sport = 'cycling';
      } else if (sessionType.includes('tri')) {
        sport = 'triathlon';
      } else {
        sport = 'running'; // Default
      }
      
      planItems.push({
        id: session.session_id,
        title: session.template_name,
        description: session.notes || `${session.type} session`,
        date: session.date,
        sport,
      });
    }
  }
  
  return planItems.length > 0 ? planItems : undefined;
}

export const sendCoachChat = async (
  message: string,
  options?: { message_id?: string }
): Promise<CoachChatResponse> => {
  // F04: Send raw message only - backend is single source of truth for slot extraction
  // F07: Support message_id for idempotency guard (optional)
  const payload: { message: string; message_id?: string } = { message };
  if (options?.message_id) {
    payload.message_id = options.message_id;
  }
  
  try {
    const response = await api.post("/coach/chat", payload) as unknown as CoachChatResponse;

    // Track first_coach_chat once per user via PostHog
    try {
      const PH_KEY = "ph_first_coach_chat";
      if (!localStorage.getItem(PH_KEY)) {
        localStorage.setItem(PH_KEY, "1");
        const { trackFirstCoachChat } = await import("@/lib/posthog");
        trackFirstCoachChat();
      }
    } catch { /* analytics should never break chat */ }

    // F05: Only process successful responses (200 OK)
    // No retry/resend logic should trigger on success
    // Axios doesn't retry by default, so this is just for clarity
    
    // FE-1: Map planned_weeks to plan_items if present (takes priority)
    if (response.planned_weeks && (!response.plan_items || response.plan_items.length === 0)) {
      response.plan_items = mapPlannedWeeksToPlanItems(response.planned_weeks);
    }

    // FE-2: Convert string-based plan_items from orchestrator to PlanItem[] format
    if (response.plan_items && response.plan_items.length > 0) {
      response.plan_items = mapStringPlanItemsToPlanItems(response.plan_items);
      // Defense in depth: strip process-step items (planner trace must not leak)
      if (response.plan_items) {
        response.plan_items = filterProcessStepPlanItems(response.plan_items);
      }
    }
    
    return response;
  } catch (error) {
    const apiError = error as ApiError;
    log.error("API", "Failed to send coach chat:", {
      message: apiError.message,
      status: apiError.status,
      code: apiError.code,
      details: apiError.details,
      userMessage: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
    });
    
    // Provide more specific error messages based on status code
    if (apiError.status === 500) {
      const enhancedError: ApiError = {
        ...apiError,
        message: "The coach service is temporarily unavailable. Please try again in a moment.",
      };
      throw enhancedError;
    }
    
    throw error;
  }
};

/**
 * Fetches calendar week data for a specific week.
 * @param date - Optional date string (YYYY-MM-DD) for the week start. If not provided, returns current week.
 * 
 * In preview mode: Returns mock calendar sessions that match mock activities.
 */
/**
 * Fetches weekly summary card with templated narrative.
 * PHASE A + B: Deterministic aggregation from execution summaries.
 * @param weekDateISO - Any date inside the week (YYYY-MM-DD format)
 */
export const fetchWeeklySummary = async (weekDateISO: string): Promise<WeeklySummaryCard> => {
  log.debug("API", "Fetching weekly summary", weekDateISO);
  try {
    const response = await calendarApi.getWeekSummary(weekDateISO);
    log.debug("API", "Weekly summary response:", response);
    
    const responseData = response.data || response;
    if (responseData && typeof responseData === 'object') {
      return responseData as WeeklySummaryCard;
    }
    
    throw new Error("Invalid response format");
  } catch (error) {
    log.error("API", "Error fetching weekly summary:", error);
    throw error;
  }
};

export const fetchCalendarWeek = async (date?: string): Promise<WeekResponse> => {
  log.debug("API", "Fetching calendar week", date ? `(requested date: ${date})` : "(current week)");
  try {
    const response = await calendarApi.getWeek(date);
    log.debug("API", "Calendar week response:", response);
    
    // Handle different response formats
    // Axios responses have a .data property
    const responseData = response.data || response;
    if (responseData && typeof responseData === 'object') {
      // If response already has the expected structure
      if ('sessions' in responseData && Array.isArray(responseData.sessions)) {
        return responseData as WeekResponse;
      }
      // If response is wrapped in a data property
      if ('data' in responseData && responseData.data && typeof responseData.data === 'object') {
        const data = responseData.data as { sessions?: unknown[]; week_start?: string; week_end?: string };
        if (data.sessions && Array.isArray(data.sessions)) {
          return {
            week_start: data.week_start || '',
            week_end: data.week_end || '',
            sessions: data.sessions as CalendarSession[],
          };
        }
      }
    }
    
    // Fallback: return empty structure if response doesn't match
    log.warn("API", "Calendar week response doesn't match expected format:", response);
    return {
      week_start: date || '',
      week_end: date || '',
      sessions: [],
    };
  } catch (error) {
    // Handle 500 errors gracefully - return empty week instead of crashing UI
    if (error && typeof error === 'object' && 'status' in error) {
      const apiError = error as { status?: number; message?: string };
      if (apiError.status === 500) {
        log.warn("API", "Calendar week returned 500 error. Returning empty week. UI will continue to work.");
        const fallbackDate = date || new Date().toISOString().split('T')[0];
        return {
          week_start: fallbackDate,
          week_end: fallbackDate,
          sessions: [],
        };
      }
    }
    log.error("API", "Failed to fetch calendar week:", error);
    throw error;
  }
};

/**
 * Fetches calendar today data.
 * Note: The API endpoint doesn't accept a date parameter - it returns today's data.
 */
export const fetchCalendarToday = async (date?: string): Promise<TodayResponse> => {
  log.debug("API", "Fetching calendar today", date ? `(requested date: ${date} - API returns today)` : "");
  try {
    const response = await calendarApi.getToday();
    const todayResponse = (response.data || response) as unknown as TodayResponse;
    
    // Debug: Log coach_insight in sessions
    if (todayResponse?.sessions) {
      todayResponse.sessions.forEach((session, idx) => {
        if (session.coach_insight) {
          log.debug("API", `Calendar today session ${idx} has coach_insight:`, {
            sessionId: session.id,
            status: session.status,
            coach_insight_length: session.coach_insight.length,
            coach_insight_preview: session.coach_insight.substring(0, 100),
          });
        } else {
          log.debug("API", `Calendar today session ${idx} NO coach_insight:`, {
            sessionId: session.id,
            status: session.status,
            has_coach_insight: !!session.coach_insight,
          });
        }
      });
    }
    
    return todayResponse;
  } catch (error) {
    // Handle 500 errors gracefully - return empty today instead of crashing UI
    if (error && typeof error === 'object' && 'status' in error) {
      const apiError = error as { status?: number; message?: string };
      if (apiError.status === 500) {
        log.warn("API", "Calendar today returned 500 error. Returning empty today. UI will continue to work.");
        const fallbackDate = date || new Date().toISOString().split('T')[0];
        return {
          date: fallbackDate,
          sessions: [],
        };
      }
    }
    log.error("API", "Failed to fetch calendar today:", error);
    throw error;
  }
};

/**
 * Fetches calendar season data.
 * 
 * In preview mode: Returns mock calendar sessions for the season.
 */
// Season summary types (narrative view)
export interface SeasonSummaryWeek {
  week_index: number;
  date_range: string;
  status: "completed" | "current" | "upcoming";
  coach_summary: string;
  key_sessions: string[];
  flags: ("fatigue" | "missed_sessions")[];
}

export interface SeasonSummaryPhase {
  name: string;
  intent: string;
  weeks: SeasonSummaryWeek[];
}

export interface SeasonSummaryGoalRace {
  name: string;
  date: string;
  weeks_to_race: number;
}

export interface SeasonSummary {
  goal_race: SeasonSummaryGoalRace | null;
  current_phase: string;
  phases: SeasonSummaryPhase[];
}

/** Canonical plan states from backend. No 503 for READY / AUTO_READY / LEARNING. */
export type SeasonPlanStatus =
  | "READY"
  | "AUTO_READY"
  | "GENERATING"
  | "LEARNING"
  | "BLOCKED";

/** Baseline artifact when data exists but no user-requested plan. Low confidence. */
export interface BaselinePlanArtifact {
  summary: string;
  current_focus: string;
  weekly_structure: string[];
  confidence: "LOW";
  note: string;
}

/** Present only when status is GENERATING (plan build job actively running). */
export interface GeneratingInfo {
  stage: string;
  percent: number | null;
}

/** Unified get_season_summary response. narrative is full summary (READY), baseline (AUTO_READY), or null. */
export interface SeasonSummaryResponse {
  status: SeasonPlanStatus;
  season: string;
  narrative: SeasonSummary | BaselinePlanArtifact | null;
  reason: string | null;
  next_steps: string[];
  generating?: GeneratingInfo | null;
}

/**
 * Fetches season summary with canonical plan state. No 503 for READY / AUTO_READY / LEARNING.
 * Returns status, narrative (or baseline artifact), reason, next_steps. BLOCKED returns 503.
 */
export const fetchSeasonSummary = async (): Promise<SeasonSummaryResponse> => {
  log.debug("API", "Fetching season summary");
  try {
    const response = await api.get<SeasonSummaryResponse>("/intelligence/season/summary");
    const responseData = response.data || response;
    return responseData as SeasonSummaryResponse;
  } catch (error) {
    const apiError = error as ApiError;
    log.error("API", "Failed to fetch season summary:", {
      message: apiError.message,
      status: apiError.status,
    });
    throw error;
  }
};

/**
 * Fetches calendar sessions for a specific date range.
 * 
 * **CRITICAL**: Use this instead of fetchCalendarSeason for UI rendering.
 * Maximum range is 45 days to prevent OOM.
 * 
 * **Safety Guards**:
 * - Validates date range before making request
 * - Throws error if range exceeds 45 days
 * - Prevents accidental season fetches
 * 
 * @param start - Start date in YYYY-MM-DD format
 * @param end - End date in YYYY-MM-DD format (inclusive)
 * @returns Sessions in the specified range
 */
export const fetchCalendarRange = async (start: string, end: string): Promise<CalendarSession[]> => {
  // Safety guard: Validate range before making request
  const startDate = new Date(start);
  const endDate = new Date(end);
  
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    throw new Error(`Invalid date format. Use YYYY-MM-DD. start=${start}, end=${end}`);
  }
  
  const rangeDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (rangeDays < 0) {
    throw new Error(`Invalid date range: end date must be after start date. start=${start}, end=${end}`);
  }
  
  // CRITICAL: Hard limit to prevent OOM
  if (rangeDays > 45) {
    throw new Error(
      `Date range exceeds maximum of 45 days. Requested: ${rangeDays} days. ` +
      `Use smaller ranges for UI rendering. start=${start}, end=${end}`
    );
  }
  
  // Log warning for large ranges (near limit)
  if (rangeDays > 35) {
    log.warn("API",
      `Large date range requested: ${rangeDays} days. ` +
      `Consider using smaller ranges for better performance.`
    );
  }
  
  log.debug("API", `Fetching calendar range: ${start} to ${end}`);
  try {
    const response = await calendarApi.getRange(start, end);
    const responseData = response.data || response;
    
    if (responseData && typeof responseData === 'object' && 'sessions' in responseData) {
      return (responseData.sessions || []) as CalendarSession[];
    }
    
    return [];
  } catch (error) {
    const apiError = error as ApiError;
    log.error("API", "Failed to fetch calendar range:", {
      message: apiError.message,
      status: apiError.status,
    });
    throw error;
  }
};

export const fetchCalendarSeason = async (): Promise<SeasonResponse> => {
  log.debug("API", "Fetching calendar season");
  try {
    const response = await calendarApi.getSeason();
    log.debug("API", "Calendar season response:", response);
    
    // Handle different response formats
    // Axios responses have a .data property
    const responseData = response.data || response;
    if (responseData && typeof responseData === 'object') {
      // If response already has the expected structure
      if ('sessions' in responseData && Array.isArray(responseData.sessions)) {
        return responseData as SeasonResponse;
      }
      // If response is wrapped in a data property
      if ('data' in responseData && responseData.data && typeof responseData.data === 'object') {
        const data = responseData.data as { sessions?: unknown[]; season_start?: string; season_end?: string; total_sessions?: number; completed_sessions?: number; planned_sessions?: number };
        if (data.sessions && Array.isArray(data.sessions)) {
          return {
            season_start: data.season_start || '',
            season_end: data.season_end || '',
            sessions: data.sessions as CalendarSession[],
            total_sessions: data.total_sessions || 0,
            completed_sessions: data.completed_sessions || 0,
            planned_sessions: data.planned_sessions || 0,
          };
        }
      }
    }
    
    // Fallback: return empty structure if response doesn't match
    log.warn("API", "Calendar season response doesn't match expected format:", response);
    return {
      season_start: new Date().toISOString().split('T')[0],
      season_end: new Date().toISOString().split('T')[0],
      sessions: [],
      total_sessions: 0,
      completed_sessions: 0,
      planned_sessions: 0,
    };
  } catch (error) {
    log.error("API", "Failed to fetch calendar season:", error);
    throw error;
  }
};

/**
 * Creates a manual training session.
 * @param session - Session data (date, type, distance or duration, optional notes)
 * @returns Created session
 */
export const createManualSession = async (session: {
  date: string;
  sport: 'Run' | 'Bike' | 'Swim' | 'Triathlon' | 'Crossfit' | 'Strength' | 'Walk' | 'Race';
  type: 'easy' | 'workout' | 'long' | 'rest' | 'threshold' | 'vo2' | 'tempo' | 'recovery' | 'race' | 'cross';
  distance_km?: number | null;
  duration_minutes?: number | null;
  notes?: string | null;
}): Promise<CalendarSession> => {
  log.debug("API", "Creating manual session:", session);
  try {
    const response = await api.post("/training/sessions/manual", session);
    return response as unknown as CalendarSession;
  } catch (error) {
    log.error("API", "Failed to create manual session:", error);
    throw error;
  }
};

/**
 * Creates manual training sessions for a week.
 * @param weekStart - Week start date (YYYY-MM-DD)
 * @param sessions - Array of session data for the week
 * @returns Created sessions
 */
export const createManualWeek = async (weekStart: string, sessions: Array<{
  date: string;
  sport: 'Run' | 'Bike' | 'Swim' | 'Triathlon' | 'Crossfit' | 'Strength' | 'Walk' | 'Race';
  type: 'easy' | 'workout' | 'long' | 'rest' | 'threshold' | 'vo2' | 'tempo' | 'recovery' | 'race' | 'cross';
  distance_km?: number | null;
  duration_minutes?: number | null;
  notes?: string | null;
}>): Promise<CalendarSession[]> => {
  log.debug("API", "Creating manual week:", { weekStart, sessionCount: sessions.length });
  try {
    const response = await api.post("/training/weeks/manual", {
      week_start: weekStart,
      sessions,
    });
    const responseData = response.data || response;
    if (Array.isArray(responseData)) {
      return responseData as CalendarSession[];
    }
    if (responseData && typeof responseData === 'object' && 'sessions' in responseData && Array.isArray(responseData.sessions)) {
      return responseData.sessions as CalendarSession[];
    }
    return [];
  } catch (error) {
    log.error("API", "Failed to create manual week:", error);
    throw error;
  }
};

/**
 * Creates manual training sessions for a season.
 * @param sessions - Array of session data for the season
 * @returns Created sessions
 */
export const createManualSeason = async (sessions: Array<{
  date: string;
  type: 'easy' | 'workout' | 'long' | 'rest' | 'threshold' | 'vo2' | 'tempo' | 'recovery' | 'race' | 'cross';
  distance_km?: number | null;
  duration_minutes?: number | null;
  notes?: string | null;
}>): Promise<CalendarSession[]> => {
  log.debug("API", "Creating manual season:", { sessionCount: sessions.length });
  try {
    const response = await api.post("/training/seasons/manual", { sessions });
    const responseData = response.data || response;
    if (Array.isArray(responseData)) {
      return responseData as CalendarSession[];
    }
    if (responseData && typeof responseData === 'object' && 'sessions' in responseData && Array.isArray(responseData.sessions)) {
      return responseData.sessions as CalendarSession[];
    }
    return [];
  } catch (error) {
    log.error("API", "Failed to create manual season:", error);
    throw error;
  }
};

/**
 * Parses workout notes into structured workout format.
 * @param notes - Free-form workout notes text
 * @param sessionType - Session type (easy, workout, long, rest)
 * @param distanceKm - Optional distance in km
 * @param durationMinutes - Optional duration in minutes
 * @returns Structured workout with steps, repeats, and intensities
 */
export interface ParsedWorkout {
  success: boolean;
  workout?: {
    steps: Array<{
      order: number;
      type: string;
      duration_seconds?: number;
      distance_meters?: number;
      intensity?: string;
      notes?: string;
    }>;
    repeats?: Array<{
      start_step: number;
      end_step: number;
      count: number;
    }>;
    total_duration_seconds?: number;
    total_distance_meters?: number;
  };
  error?: string;
  confidence?: number;
}

export const parseWorkoutNotes = async (
  notes: string,
  sessionType: 'easy' | 'workout' | 'long' | 'rest' | 'threshold' | 'vo2' | 'tempo' | 'recovery' | 'race' | 'cross',
  distanceKm?: number | null,
  durationMinutes?: number | null
): Promise<ParsedWorkout> => {
  log.debug("API", "Parsing workout notes:", { notes, sessionType, distanceKm, durationMinutes });
  try {
    const response = await api.post("/training/sessions/parse-notes", {
      notes,
      session_type: sessionType,
      distance_km: distanceKm,
      duration_minutes: durationMinutes,
    });
    return response as unknown as ParsedWorkout;
  } catch (error) {
    log.info("API", "Workout notes parsing unavailable");
    throw error;
  }
};

/**
 * Exports a structured workout to Garmin FIT format.
 * @param sessionId - The ID of the session to export
 * @returns Blob containing the FIT file
 */
export const exportWorkoutToFIT = async (sessionId: string): Promise<Blob> => {
  log.debug("API", "Exporting workout to FIT:", sessionId);
  try {
    const response = await api.get(`/training/sessions/${sessionId}/export/fit`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  } catch (error) {
    log.error("API", "Failed to export workout to FIT:", error);
    throw error;
  }
};

/**
 * Gets structured workout data for a session (including parsed steps and graph time series).
 * @param sessionId - The ID of the session
 * @returns Structured workout with steps and time series data
 */
export interface StructuredWorkoutResponse {
  session_id: string;
  workout?: {
    steps: Array<{
      order: number;
      type: string;
      duration_seconds?: number;
      distance_meters?: number;
      intensity?: string;
      notes?: string;
    }>;
    repeats?: Array<{
      start_step: number;
      end_step: number;
      count: number;
    }>;
  };
  time_series?: {
    time: number[];
    intensity: (string | null)[];
    distance?: number[];
  };
  parse_status: 'success' | 'warning' | 'failed';
  parse_error?: string;
}

/**
 * Updates the status of a calendar session.
 * @param sessionId - The ID of the session to update
 * @param status - The new status: "completed", "skipped", "deleted", or "planned"
 * @param completedActivityId - Optional ID of the completed activity to link to the session
 * @param confirmed - Optional flag to confirm the operation (for PROPOSAL_ONLY retries)
 */
export const updateSessionStatus = async (
  sessionId: string,
  status: "completed" | "skipped" | "deleted" | "planned",
  completedActivityId?: string,
  confirmed?: boolean
): Promise<CalendarSession | WriteResponse<CalendarSession>> => {
  log.debug("API", "Updating session status:", { sessionId, status, completedActivityId, confirmed });

  // Lifecycle deletion should use explicit delete endpoints.
  // PATCH /calendar/sessions/{id}/status only supports lifecycle mutation for real planned-session rows.
  if (status === "deleted") {
    try {
      // Preferred path: centralized calendar lifecycle endpoint
      await calendarApi.updateSessionStatus(sessionId, { status: "deleted" });
      return {
        id: sessionId,
        date: "",
        time: null,
        type: "Run",
        title: "",
        duration_minutes: null,
        distance_km: null,
        intensity: null,
        status: "deleted",
        notes: null,
      } as CalendarSession;
    } catch (error) {
      // Fallbacks for mixed storage shapes
      const apiError = error as { status?: number; message?: string };
      if (apiError.status === 404) {
        try {
          await calendarApi.deletePlannedSession(sessionId);
        } catch {
          await activitiesApi.delete(sessionId);
        }
        return {
          id: sessionId,
          date: "",
          time: null,
          type: "Run",
          title: "",
          duration_minutes: null,
          distance_km: null,
          intensity: null,
          status: "deleted",
          notes: null,
        } as CalendarSession;
      }
      throw error;
    }
  }

  try {
    const payload: {
      status: string;
      completed_activity_id?: string;
      confirmed?: boolean;
    } = {
      status,
    };
    
    if (completedActivityId) {
      payload.completed_activity_id = completedActivityId;
    }
    
    // Add confirmed flag if provided (for retry after PROPOSAL_ONLY)
    if (confirmed === true) {
      payload.confirmed = true;
    }

    const response = await calendarApi.updateSessionStatus(sessionId, payload);
    log.debug("API", "Session status response:", response);
    
    // Check if response is PROPOSAL_ONLY or contains proposal
    const responseData = response.data || response;
    
    // Return response as-is (may be PROPOSAL_ONLY or CalendarSession)
    return responseData as CalendarSession | WriteResponse<CalendarSession>;
  } catch (error) {
    log.error("API", "Failed to update session status:", error);
    throw error;
  }
};

/**
 * Updates a planned session's scheduled date.
 * Only updates the date - does not modify activities or completed workout timestamps.
 * 
 * @param sessionId - The ID of the planned session to update
 * @param scheduledDate - New scheduled date in YYYY-MM-DD format
 * @param startTime - Optional new start time (HH:MM format)
 * @param orderInDay - Optional new order within the day
 * @returns Updated CalendarSession
 */
export const updatePlannedSessionDate = async (
  sessionId: string,
  scheduledDate: string,
  startTime?: string | null,
  orderInDay?: number | null
): Promise<CalendarSession> => {
  void orderInDay;
  // HARD ASSERT: planned_session_id is required to move session
  if (!sessionId) {
    throw new Error("planned_session_id required to move session");
  }
  
  log.debug("API", "Updating planned session date:", { sessionId, scheduledDate, startTime, orderInDay });
  try {
    const payload: Record<string, unknown> = { date: scheduledDate };
    if (startTime !== undefined) payload.time = startTime;

    // CRITICAL: Use planned_session_id in endpoint path
    // NEVER use /workouts/{id} or /calendar-sessions/{id}
    const response = await calendarApi.updatePlannedSession(sessionId, {
      date: scheduledDate,
      time: startTime ?? undefined,
    });
    log.debug("API", "Planned session date updated:", response);
    
    const responseData = response.data || response;
    return responseData as CalendarSession;
  } catch (error) {
    const apiError = error as { status?: number };
    // Fallback for activity-backed planned items (no planned_sessions row)
    if (apiError.status === 404) {
      const response = await api.patch(`/activities/${sessionId}`, {
        scheduled_date: scheduledDate,
      });
      log.debug("API", "Activity-backed planned item date updated:", response);
      const responseData = response.data || response;
      return responseData as CalendarSession;
    }
    log.error("API", "Failed to update planned session date:", error);
    throw error;
  }
};

/**
 * Checks for recent activities (last 48 hours) on refresh or new session.
 * Runs in background to ensure today's activities are synced.
 */
export const checkRecentActivities = async (): Promise<{
  success: boolean;
  message: string;
  last_sync: string;
}> => {
  log.debug("API", "Checking for recent activities");
  try {
    await settingsApi.syncCheck();
    return {
      success: true,
      message: "Recent activities check completed",
      last_sync: new Date().toISOString(),
    };
  } catch (error) {
    log.error("API", "Failed to check recent activities:", error);
    throw error;
  }
};

/**
 * User-initiated sync button.
 * Fetches activities since last sync (or last 48 hours for safety), runs in background.
 */
export const syncActivitiesNow = async (): Promise<{
  success: boolean;
  message: string;
  provider: string;
  last_sync: string | null;
}> => {
  log.debug("API", "User-initiated sync");
  try {
    const result = await settingsApi.syncNow();
    return result;
  } catch (error) {
    log.error("API", "Failed to sync activities:", error);
    throw error;
  }
};

/**
 * Gets coaching recommendations from real LLM.
 */
export const fetchCoachRecommendations = async (): Promise<{
  recommendations: Array<{
    id: string;
    priority: "high" | "medium" | "low";
    category: "intensity" | "volume" | "recovery" | "structure" | "general";
    recommendation: string;
    rationale: string;
    timestamp: string;
  }>;
  total: number;
}> => {
  log.debug("API", "Fetching coach recommendations");
  try {
    const response = await api.get("/coach/recommendations");
    return response as unknown as {
      recommendations: Array<{
        id: string;
        priority: "high" | "medium" | "low";
        category: "intensity" | "volume" | "recovery" | "structure" | "general";
        recommendation: string;
        rationale: string;
        timestamp: string;
      }>;
      total: number;
    };
  } catch (error) {
    log.error("API", "Failed to fetch coach recommendations:", error);
    throw error;
  }
};

/**
 * Asks the coach a question using real LLM.
 */
export const askCoach = async (
  message: string,
  context?: { days?: number; days_to_race?: number | null }
): Promise<{
  reply: string;
  intent: string;
  confidence: number;
}> => {
  log.debug("API", "Asking coach question");
  try {
    const payload: { message: string; context?: { days?: number; days_to_race?: number | null } } = { message };
    if (context) {
      payload.context = context;
    }
    const response = await api.post("/coach/ask", payload);
    return response as unknown as {
      reply: string;
      intent: string;
      confidence: number;
    };
  } catch (error) {
    log.error("API", "Failed to ask coach:", error);
    throw error;
  }
};

/**
 * Triggers Strava data sync.
 */
export const syncStravaData = async (): Promise<void> => {
  log.debug("API", "Syncing Strava data");
  try {
    await api.post("/strava/sync");
  } catch (error) {
    log.error("API", "Failed to sync Strava data:", error);
    throw error;
  }
};

/**
 * Safe API call wrapper that handles undefined/null responses gracefully.
 * Never assumes success - always checks if result is valid.
 * 
 * @param fn - Function that returns a Promise
 * @returns Promise that resolves to the result or null on error
 */
export async function safeApiCall<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    const result = await fn();
    // Check if result is actually valid (not undefined/null)
    if (result === undefined || result === null) {
      log.warn("API", "safeApiCall: Function returned undefined/null");
      return null;
    }
    return result;
  } catch (err) {
    log.error("API", "safeApiCall error:", err);
    return null;
  }
}

const normalizeError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    // Backend error format: {"error": "error_code", "message": "human readable message"}
    // Or: {"detail": {"error": "code", "message": "..."}} or {"detail": {"reason": "...", "message": "..."}}
    const axiosError = error as AxiosError<{ detail?: string | unknown[] | { error?: string; reason?: string; message?: string }; message?: string | unknown[]; error?: string | unknown[] }>;
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;
    
    const extractMessage = (value: unknown): string => {
      if (typeof value === "string") {
        return value;
      }
      if (Array.isArray(value)) {
        return value.map((item) => {
          if (typeof item === "string") {
            return item;
          }
          if (item && typeof item === "object" && "msg" in item) {
            return String(item.msg);
          }
          return String(item);
        }).join(", ");
      }
      if (value && typeof value === "object" && "msg" in value) {
        return String(value.msg);
      }
      return String(value);
    };
    
    let message = "An unexpected error occurred";
    let errorDetails: { error?: string; reason?: string; message?: string } | undefined;
    
    // Extract structured error details from backend response
    // Backend auth errors can be in format:
    // - {"detail": {"error": "code", "message": "..."}}
    // - {"detail": {"reason": "...", "message": "..."}}
    // - {"error": "code", "message": "..."}
    if (data?.detail) {
      if (typeof data.detail === "object" && data.detail !== null && !Array.isArray(data.detail)) {
        // Structured error object
        const detailObj = data.detail as { error?: string; reason?: string; message?: string };
        errorDetails = detailObj;
        message = detailObj.message || extractMessage(data.detail);
      } else {
        // String or array detail
        message = extractMessage(data.detail);
      }
    } else if (data?.message) {
      message = extractMessage(data.message);
    } else if (data?.error) {
      message = extractMessage(data.error);
    } else if (axiosError.code === "ECONNABORTED" || (axiosError.message && axiosError.message.includes("timeout"))) {
      message = "Request timed out. Please try again.";
    } else if (axiosError.code === "ERR_NETWORK" || !axiosError.response) {
      const requestUrl = axiosError.config?.url || axiosError.request?.responseURL || "";
      const baseURL = axiosError.config?.baseURL || "";
      const fullUrl = requestUrl.startsWith('http') ? requestUrl : `${baseURL}${requestUrl}`;
      const isCrossOrigin = requestUrl && !requestUrl.startsWith(window.location.origin);
      
      // Enhanced logging for network errors to help diagnose connectivity issues
      log.error("API", "ERR_NETWORK details:", {
        code: axiosError.code,
        message: axiosError.message,
        requestUrl: fullUrl,
        baseURL: baseURL,
        isCrossOrigin: isCrossOrigin,
        requestStatus: axiosError.request?.status,
        requestReadyState: axiosError.request?.readyState,
        hasResponse: !!axiosError.response,
        error: axiosError,
      });
      
      if (isCrossOrigin && axiosError.message && axiosError.message.includes("CORS") || 
          (typeof axiosError.request !== "undefined" && axiosError.request.status === 0)) {
        message = "Network error: Unable to connect to the backend server. This may be a temporary connectivity issue.";
      } else {
        message = "Network error. Please check your connection and ensure the backend server is running.";
      }
    } else if (status === 401) {
      message = "Authentication required. Please log in.";
    } else if (status === 403) {
      message = "You don't have permission to perform this action.";
    } else if (status === 404) {
      message = "Resource not found.";
    } else if (status === 500) {
      // Check for database/schema errors in the error message
      const errorStr = JSON.stringify(data || axiosError.message || '').toLowerCase();
      if (errorStr.includes('column') && errorStr.includes('does not exist') ||
          errorStr.includes('programmingerror') ||
          errorStr.includes('undefinedcolumn') ||
          errorStr.includes('migration')) {
        message = "Database configuration error. The server is being updated. Please try again in a few moments.";
      } else {
        message = "Server error. Please try again later.";
      }
    } else if (axiosError.message) {
      message = axiosError.message;
    }

    const base = axiosError.config?.baseURL ?? "";
    const path = axiosError.config?.url ?? "";
    const requestUrl = path.startsWith("http") ? path : `${base}${path}`;

    return {
      message,
      status,
      code: axiosError.code,
      details: errorDetails || data,
      requestUrl: requestUrl || undefined,
    };
  }
  
  if (error instanceof Error) {
    return {
      message: error.message,
      details: error,
    };
  }
  
  return {
    message: "An unknown error occurred",
    details: error,
  };
};

// Request interceptor: Sets Content-Type, conversation ID, and Authorization headers
// AUTHENTICATION:
// - Web: HTTP-only cookies (via withCredentials: true)
// - Mobile: Bearer tokens in Authorization header (from secure storage)
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Ensure headers object exists (axios may not initialize it)
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }
    
    // Ensure Content-Type is set for POST/PUT requests with data
    if ((config.method === 'post' || config.method === 'put' || config.method === 'patch') && config.data) {
      const contentType = 'application/json';
      if (typeof (config.headers as { set?: (name: string, value: string) => void }).set === 'function') {
        (config.headers as { set: (name: string, value: string) => void }).set('Content-Type', contentType);
      } else {
        (config.headers as Record<string, string>)['Content-Type'] = contentType;
      }
    }
    
    // Add Authorization header for mobile (Bearer token)
    if (isNative()) {
      const { getAccessToken } = await import('./tokenStorage');
      const token = await getAccessToken();
      if (token) {
        if (typeof (config.headers as { set?: (name: string, value: string) => void }).set === 'function') {
          (config.headers as { set: (name: string, value: string) => void }).set('Authorization', `Bearer ${token}`);
        } else {
          (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
        }
      }
    }
    
    // Add X-Client header to help backend detect mobile clients
    if (isNative()) {
      if (typeof (config.headers as { set?: (name: string, value: string) => void }).set === 'function') {
        (config.headers as { set: (name: string, value: string) => void }).set('X-Client', 'mobile');
      } else {
        (config.headers as Record<string, string>)['X-Client'] = 'mobile';
      }
    }
    
    // Add conversation ID header to every request
    const conversationId = getConversationId();
    if (typeof (config.headers as { set?: (name: string, value: string) => void }).set === 'function') {
      (config.headers as { set?: (name: string, value: string) => void }).set('X-Conversation-Id', conversationId);
    } else {
      (config.headers as Record<string, string>)['X-Conversation-Id'] = conversationId;
    }
    
    // Dev-only logging for verification
    if (import.meta.env.DEV) {
      log.debug("API", 'X-Conversation-Id:', conversationId, 'URL:', config.url, 'Mobile:', isNative());
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we've already logged a CORS error to avoid console spam
let corsErrorLogged = false;

// Custom event to trigger navigation from React Router context
const createNavigationEvent = (path: string) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-redirect', { detail: { path } }));
  }
};

// Custom event to trigger logout from AuthContext
const triggerLogoutEvent = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-logout', { detail: {} }));
  }
};

api.interceptors.response.use(
  (response) => {
    const requestUrl = response.config?.url || '';
    const isMeEndpoint = requestUrl === '/me' || requestUrl.endsWith('/me');
    
    // Guard against undefined/null responses
    // Backend may return 200 with broken/empty payload
    if (response.data === undefined || response.data === null) {
      log.warn("API", "Response interceptor: response.data is undefined/null", {
        status: response.status,
        url: requestUrl,
      });
      
      // For /me endpoint, return null explicitly (means not authenticated)
      // For other endpoints, return {} to prevent .then() errors while maintaining compatibility
      if (isMeEndpoint) {
        return null;
      }
      return {};
    }
    
    // Log /me responses for debugging
    if (isMeEndpoint) {
      log.debug("API", "/me response interceptor:", {
        url: requestUrl,
        status: response.status,
        data: response.data,
        dataType: typeof response.data,
      });
    }
    
    return response.data;
  },
  (error) => {
    const normalizedError = normalizeError(error);
    const requestUrl = error?.config?.url || '';
    const isMeEndpoint = requestUrl === '/me' || requestUrl.endsWith('/me');
    
    // CRITICAL: Frontend invariant - NEVER logout on 404
    // Status 200 = authenticated
    // Status 401 = logout
    // Status 404 = ignore (backend bug, don't logout)
    // Status 500 = show error
    if (isMeEndpoint) {
      if (normalizedError.status === 401) {
        // 401 = authentication failure (cookie missing/invalid)
        // CRITICAL: Only trigger logout/navigation if NOT on a public route
        // During bootstrap, /me 401 is expected and should NOT trigger navigation
        // For HashRouter (Capacitor), path is in hash, not pathname
        const getCurrentPath = (): string => {
          if (typeof window === 'undefined') return '';
          // Check hash first (HashRouter for Capacitor)
          if (window.location.hash) {
            const hashPath = window.location.hash.replace('#', '');
            return hashPath || '/';
          }
          // Fallback to pathname (BrowserRouter for web)
          return window.location.pathname || '/';
        };
        
        const currentPath = getCurrentPath();
        const publicPaths = ["/login", "/signup", "/onboarding", "/", ""];
        const isPublicPath = publicPaths.some(path => {
          const normalizedPath = path === "" ? "/" : path;
          return currentPath === normalizedPath || currentPath.startsWith(`${normalizedPath}/`);
        });
        
        // Only trigger logout event (don't navigate if already on public route)
        // The logout event updates auth state, but navigation is handled by router guards
        triggerLogoutEvent();
        
        // Only navigate if NOT on a public route (let router guards handle public routes)
        if (!isPublicPath) {
          log.debug("API", "/me 401 on protected route, triggering navigation to /login");
          createNavigationEvent("/login");
        } else {
          log.debug("API", "/me 401 on public route, skipping navigation (router guards will handle)");
        }
        
        return Promise.reject(normalizedError);
      }
      
      if (normalizedError.status === 404) {
        // 404 = backend bug, ignore - NEVER logout
        log.error("API",
          "/me returned 404 - BACKEND CONTRACT VIOLATION. " +
          "/me must NEVER return 404. It should return 200 (authenticated), 401 (not authenticated), or 500 (server error). " +
          "Frontend invariant: NOT logging out on 404. Backend should be fixed to return 401 instead of 404."
        );
        // Return resolved promise to prevent logout
        // This ensures the frontend never logs out on 404
        return Promise.resolve({ data: null, status: 404 });
      }
      
      // For other errors on /me (500, network, etc.), reject normally
      return Promise.reject(normalizedError);
    }
    
    // Handle 401 for non-/me endpoints: Trigger logout event
    // Authentication is handled by HTTP-only cookies, so we just need to update React state
    if (normalizedError.status === 401) {
      // Trigger logout event for AuthContext to handle
      // This ensures React state is updated when auth fails
      triggerLogoutEvent();
      
      // Trigger navigation to login (unless on public pages)
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const publicPaths = ["/login", "/signup", "/onboarding"];
      const isPublicPath = publicPaths.some(path => 
        currentPath === path || currentPath.startsWith(`${path}/`)
      );
      
      if (!isPublicPath) {
        createNavigationEvent("/login");
      }
      
      return Promise.reject(normalizedError);
    }
    
    // 503 means service unavailable / data not ready - this is expected, not an error
    if (normalizedError.status === 503) {
      // Don't log 503 errors - they're expected when data isn't ready yet
      return Promise.reject(normalizedError);
    }
    
    // CORS/network errors - should be rare now that backend CORS is configured
    // Log them for debugging but don't spam the console
    if (normalizedError.code === "ERR_NETWORK" || 
        (normalizedError.message && normalizedError.message.includes("CORS"))) {
      // Log once per session to help identify any remaining CORS issues
      if (!corsErrorLogged) {
        log.warn("API", "CORS/Network error detected. If this persists, check backend CORS configuration.");
        corsErrorLogged = true;
      }
    } else {
      // Log other errors normally (but not 503)
      log.error("API", "Request failed:", normalizedError);
    }
    
    return Promise.reject(normalizedError);
  }
);

/**
 * Fetches a workout by ID.
 * PHASE F2: Activities always render Workout panel - this endpoint is required.
 * @param workoutId - The ID of the workout to fetch
 * @returns Workout data
 */
export const fetchWorkout = async (workoutId: string): Promise<PlannedWorkout & { steps?: Array<{
  id: string;
  order: number;
  name: string;
  type: string;
  duration_seconds?: number | null;
  distance_meters?: number | null;
  purpose?: string | null;
  instructions?: string | null;
  intensity?: string | null;
  target_metric?: string | null;
  target_min?: number | null;
  target_max?: number | null;
}> }> => {
  log.debug("API", "Fetching workout:", workoutId);
  try {
    const response = await api.get(`/workouts/${workoutId}`);
    return response as unknown as PlannedWorkout & { steps?: Array<{
      id: string;
      order: number;
      name: string;
      type: string;
      duration_seconds?: number | null;
      distance_meters?: number | null;
      purpose?: string | null;
      instructions?: string | null;
      intensity?: string | null;
      target_metric?: string | null;
      target_min?: number | null;
      target_max?: number | null;
    }> };
  } catch (error) {
    log.error("API", "Failed to fetch workout:", error);
    throw error;
  }
};

/**
 * Fetches workout execution data.
 * PHASE F2: Activities always render Workout panel with Execution tab.
 * @param workoutId - The ID of the workout
 * @returns Execution data
 */
export const fetchWorkoutExecution = async (workoutId: string): Promise<{
  completed: boolean;
  actualActivityId?: string;
  executionDate?: string;
  metrics?: {
    duration?: number;
    distance?: number;
    avgHeartRate?: number;
    avgPower?: number;
    trainingLoad?: number;
  };
}> => {
  log.debug("API", "Fetching workout execution:", workoutId);
  try {
    const response = await api.get(`/workouts/${workoutId}/execution`);
    return response as unknown as {
      completed: boolean;
      actualActivityId?: string;
      executionDate?: string;
      metrics?: {
        duration?: number;
        distance?: number;
        avgHeartRate?: number;
        avgPower?: number;
        trainingLoad?: number;
      };
    };
  } catch (error) {
    log.error("API", "Failed to fetch workout execution:", error);
    throw error;
  }
};

/**
 * Fetches workout compliance data.
 * PHASE F2: Activities always render Workout panel with Compliance tab.
 * @param workoutId - The ID of the workout
 * @returns Compliance data
 */
export const fetchWorkoutCompliance = async (workoutId: string): Promise<{
  overall_compliance_pct: number;
  completed: boolean;
  total_pause_seconds: number;
  steps?: Array<{
    order: number;
    compliance_pct: number;
    time_in_range_seconds: number;
    overshoot_seconds: number;
    undershoot_seconds: number;
    pause_seconds: number;
  }>;
}> => {
  log.debug("API", "Fetching workout compliance:", workoutId);
  try {
    const response = await api.get(`/workouts/${workoutId}/compliance`);
    return response as unknown as {
      overall_compliance_pct: number;
      completed: boolean;
      total_pause_seconds: number;
      steps?: Array<{
        order: number;
        compliance_pct: number;
        time_in_range_seconds: number;
        overshoot_seconds: number;
        undershoot_seconds: number;
        pause_seconds: number;
      }>;
    };
  } catch (error) {
    log.error("API", "Failed to fetch workout compliance:", error);
    throw error;
  }
};

/**
 * Updates a workout's scheduled date.
 * PHASE F5: Drag & drop updates workout date via this endpoint.
 * @param workoutId - The ID of the workout
 * @param scheduledDate - New scheduled date in YYYY-MM-DD format
 * @returns Updated workout
 */
export const updateWorkoutDate = async (
  workoutId: string,
  scheduledDate: string
): Promise<PlannedWorkout> => {
  // Deprecated path retained for compatibility. Workout IDs are not mutable via /workouts.
  // Treat incoming workoutId as planned_session_id fallback.
  const updated = await updatePlannedSessionDate(workoutId, scheduledDate);
  return {
    id: updated.id,
    date: updated.date,
    sport: updated.type as PlannedWorkout["sport"],
    intent: "easy",
    title: updated.title,
    description: updated.notes ?? "",
    duration: updated.duration_minutes ?? 0,
    completed: updated.status === "completed",
  };
};

/**
 * Plan Inspector API types
 */
export interface PlanModification {
  type: string;
  affected_session: string | null;
  delta: string;
  reason: string;
  trigger: string;
}

export interface WeekInspect {
  week_index: number;
  date_range: [string, string];
  status: "completed" | "current" | "upcoming";
  intended_focus: string;
  planned_key_sessions: string[];
  modifications: PlanModification[];
}

export interface PlanPhaseInspect {
  name: string;
  intent: string;
  weeks: WeekInspect[];
}

export interface PlanSnapshot {
  objective: string;
  anchor_type: "race" | "objective";
  anchor_title: string;
  anchor_date: string | null;
  current_phase: string | null;
  total_weeks: number;
  weekly_structure: Record<string, string>;
}

export interface CoachAssessment {
  summary: string;
  confidence: "low" | "medium" | "high";
}

export interface PlanChangeLogItem {
  date: string;
  change_type: string;
  description: string;
}

export interface PlanInspectResponse {
  plan_snapshot: PlanSnapshot;
  phases: PlanPhaseInspect[];
  current_week: WeekInspect | null;
  coach_assessment: CoachAssessment | null;
  change_log: PlanChangeLogItem[];
}

/**
 * Get plan inspection data (dev/admin only).
 * @param athleteId - Optional athlete ID (defaults to user's athlete)
 * @returns Plan inspection data
 */
export const getPlanInspect = async (athleteId?: number): Promise<PlanInspectResponse> => {
  log.debug("API", "Fetching plan inspect:", { athleteId });
  try {
    const params = athleteId ? { athlete_id: athleteId } : {};
    const response = await api.get("/plan/inspect", { params });
    return response as unknown as PlanInspectResponse;
  } catch (error) {
    log.error("API", "Failed to fetch plan inspect:", error);
    throw error;
  }
};

// ─── ADVANCED ANALYTICS ENDPOINTS ──────────────────────────────
// Re-exported from analyticsApi.ts for backward compatibility (Track 6.3).
export {
  fetchAnalyticsMetrics,
  fetchPerformanceAnalytics,
  backfillRecentActivityStreams,
  fetchPowerDurationCurve,
  fetchPeakPowerRecords,
  fetchEfficiencyTrend,
  fetchZoneDistribution,
  fetchInsightsOverviewAdvanced,
  fetchInsightsComplianceAdvanced,
  fetchRiskAssessment,
} from "./analyticsApi";

export type {
  PerformanceAnalyticsResponse,
  RiskAssessmentPayload,
} from "./analyticsApi";
